// ═══════════════════════════════════════════════════════════════════════════════
// PAGE BOUTIQUE — ESPACE PRO AbracadaBati
// Page de présentation commerciale — Convaincre le pro de s'abonner
// Design identique à Super Assistant & Équipe Agents IA
// Route: /pro/boutique
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import UnifiedHeader from '../../components/layout/UnifiedHeader';
import EspaceProNav from '../../components/layout/EspaceProNav';
import EspaceProSectionsNav, { EspaceProSectionsNavSpacer } from '../../components/layout/EspaceProSectionsNav';
import ImmersiveEspaceProWrapper from '../../components/layout/ImmersiveEspaceProWrapper';
import { fadeUpHero, fadeUp, listItem, STAGGER, DURATION, EASE } from '../../utils/animations';

// ═══════════════════════════════════════════════════════════════════════════════
// COULEURS — IDENTIQUES AUX AUTRES PAGES ESPACE PRO
// ═══════════════════════════════════════════════════════════════════════════════
const C = {
  navy: '#1E3A5F',
  navyDark: '#1a2a4a',
  navyLight: '#2d5a8a',
  orange: '#F97316',
  orangeDark: '#E8650A',
  green: '#22c55e',
  cream: '#FFF8F4',
  white: '#FFFFFF',
  darkBg: '#0d1b2e',
  peachBorder: '#fce4d4',
};

// ═══════════════════════════════════════════════════════════════════════════════
// DONNÉES
// ═══════════════════════════════════════════════════════════════════════════════

const PILIERS = [
  { emoji: '🏪', text: 'Ta boutique dans AbracadaBati' },
  { emoji: '💰', text: '0% de commission — pour toujours' },
  { emoji: '🚪', text: '2 portes d\'entrée — marketplace + ton domaine' },
  { emoji: '⚡', text: 'Ton équipe IA dédiée à ta boutique' },
];

const TIMELINE_STEPS = [
  { day: 1, title: 'On prend en main ton dossier', desc: 'On collecte ton logo, ta charte, tes infos. Tu n\'as qu\'à nous envoyer ce que tu as.' },
  { day: 2, title: 'Ta boutique prend forme', desc: 'Ton site vitrine est créé à ton image. Tes couleurs, ton logo, tes prestations.' },
  { day: 3, title: 'Ton catalogue est structuré', desc: 'Tes produits sont importés, organisés, photographiés si besoin.' },
  { day: 4, title: 'Ton équipe IA est formée', desc: 'On forme ton Super Assistant et tes agents à ton métier, tes tarifs, ton fonctionnement.' },
  { day: 5, title: 'Tes outils sont connectés', desc: 'Email, réseaux sociaux, agenda, module de paiement — tout est relié.' },
  { day: 6, title: 'Ton nom de domaine est activé', desc: 'dupont-plomberie.com pointe vers ta boutique. Les deux portes sont ouvertes.' },
  { day: 7, title: 'Tu es en ligne', desc: 'Ta boutique est live. Ton équipe IA tourne. Tes clients peuvent acheter.' },
];

const DOMAINE_OPTIONS = [
  { emoji: '🌐', title: 'Tu n\'as pas de site', desc: 'On t\'achète ton nom de domaine et on crée ton site complet. dupont-plomberie.com — c\'est toi.', badge: 'Nom de domaine offert 1 an (5 ans pour les Fondateurs)', badgeColor: C.green, dark: false },
  { emoji: '🔗', title: 'Tu as déjà un site', desc: 'On crée ta boutique AbracadaBati en sous-domaine. dupont.abracadabati.com — une porte d\'entrée supplémentaire.', badge: 'Zéro conflit avec ton site existant', badgeColor: C.orangeDark, dark: false },
  { emoji: '🚀', title: 'Tu veux les deux', desc: 'Ton site existant + ta boutique AbracadaBati + un nouveau domaine complémentaire. Trois portes d\'entrée.', badge: 'Exposition maximale', badgeColor: C.orangeDark, dark: true },
];

const TABLEAU_FEATURES = [
  { label: 'Boutique dans AbracadaBati', gratuit: true, pro: true, business: true, premium: true },
  { label: 'Prestations de services', gratuit: '25 max', pro: '50 max', business: 'Illimitées', premium: 'Illimitées' },
  { label: 'Produits e-commerce', gratuit: false, pro: '100', business: '5 000', premium: '50 000' },
  { label: '0% commission', gratuit: true, pro: true, business: true, premium: true },
  { label: 'Nom de domaine propre', gratuit: false, pro: '1 an', business: '1 an', premium: '1 an' },
  { label: 'Module paiement Stripe', gratuit: true, pro: true, business: true, premium: true },
  { label: 'Module paiement propre', gratuit: false, pro: false, business: 'Option', premium: 'Option' },
  { label: 'Réseau social', gratuit: true, pro: true, business: true, premium: true },
  { label: 'Blog', gratuit: false, pro: true, business: true, premium: true },
  { label: 'Groupe via Feed', gratuit: false, pro: false, business: false, premium: true },
  { label: 'Super Assistant IA', gratuit: true, pro: 'Boosté', business: 'Boosté', premium: 'Boosté' },
  { label: 'Agents IA dédiés', gratuit: false, pro: false, business: '4 agents', premium: '12 agents' },
  { label: 'Installation en 7 jours', gratuit: false, pro: '+250€', business: '+500€', premium: '+750€' },
  { label: 'Utilisateurs inclus', gratuit: '1', pro: '5', business: '20', premium: '50' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES GLOBAUX
// ═══════════════════════════════════════════════════════════════════════════════
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
    
    .boutique-root { font-family: 'DM Sans', sans-serif; color: ${C.navyDark}; background: #072B57; }
    .boutique-root * { box-sizing: border-box; }
    
    .boutique-section { padding: clamp(64px, 10vw, 120px) clamp(20px, 5vw, 40px); }
    .boutique-container { max-width: 1280px; margin: 0 auto; padding: 0 24px; }
    
    .boutique-h1 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 800;
      font-size: clamp(36px, 6vw, 72px);
      line-height: 1.05;
      margin-bottom: 24px;
    }
    .boutique-h2 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 800;
      font-size: clamp(32px, 5vw, 56px);
      line-height: 1.1;
      margin-bottom: 20px;
    }
    .boutique-h3 {
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: clamp(20px, 3vw, 28px);
      line-height: 1.2;
    }
    
    .boutique-lead {
      font-family: 'DM Sans', sans-serif;
      font-weight: 300;
      font-size: clamp(16px, 2vw, 20px);
      line-height: 1.6;
      max-width: 640px;
    }
    
    .boutique-label {
      display: inline-block;
      padding: 10px 20px;
      border-radius: 999px;
      font-family: 'DM Sans', sans-serif;
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 20px;
    }
    
    .boutique-card {
      background: ${C.white};
      border: 1px solid ${C.peachBorder};
      border-radius: 16px;
      padding: 28px;
      transition: all 0.3s ease;
    }
    .boutique-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 40px rgba(26, 42, 74, 0.12);
    }
    
    .boutique-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 16px 32px;
      border-radius: 999px;
      font-family: 'DM Sans', sans-serif;
      font-weight: 700;
      font-size: 16px;
      text-decoration: none;
      transition: all 0.2s ease;
      cursor: pointer;
      border: none;
    }
    .boutique-btn:hover { transform: scale(1.03); }
    
    /* Responsive Hero 3-colonnes */
    @media (max-width: 1400px) {
      .hero-phone-col img { 
        max-width: 340px !important; 
      }
    }
    @media (max-width: 1200px) {
      .hero-grid-3 { 
        grid-template-columns: 1fr 1fr !important; 
        gap: 40px !important;
      }
      .hero-features-col { display: none !important; }
      .hero-phone-col { 
        display: flex !important;
        justify-content: center !important;
      }
      .hero-phone-col img { 
        max-width: 300px !important; 
      }
    }
    @media (max-width: 900px) {
      .hero-phone-col img { 
        max-width: 260px !important; 
      }
    }
    @media (max-width: 768px) {
      .hero-grid-3 { 
        grid-template-columns: 1fr !important; 
        text-align: center;
      }
      .hero-phone-col { 
        display: flex !important;
        order: -1;
        margin-bottom: 32px;
      }
      .hero-phone-col img { 
        max-width: 220px !important; 
      }
      .hero-features-col { 
        display: none !important;
      }
      .hero-stats { 
        justify-content: center !important; 
        flex-wrap: wrap;
        gap: 16px;
      }
      .hero-stats > div {
        border-left: none !important;
        padding: 0 12px !important;
      }
    }
    @media (max-width: 480px) {
      .hero-phone-col img { 
        max-width: 320px !important; 
      }
      .hero-phone-col {
        width: 100% !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
      }
      .hide-on-mobile {
        display: none !important;
      }
    }
    
    /* Responsive */
    @media (max-width: 1024px) {
      .boutique-grid-3 { grid-template-columns: repeat(2, 1fr) !important; }
      .boutique-grid-2 { grid-template-columns: 1fr !important; }
    }
    @media (max-width: 768px) {
      .boutique-grid-3 { grid-template-columns: 1fr !important; }
      .boutique-piliers { flex-direction: column !important; }
      .boutique-table-desktop { display: none !important; }
      .boutique-table-mobile { display: block !important; }
    }
    @media (min-width: 769px) {
      .boutique-table-mobile { display: none !important; }
    }
    
    /* Responsive portes-cards-grid */
    @media (max-width: 1024px) {
      .portes-cards-grid { gap: 20px !important; }
      .portes-cards-grid > div { width: 280px !important; }
    }
    @media (max-width: 768px) {
      .portes-cards-grid { 
        flex-direction: column !important; 
        gap: 20px !important;
      }
      .portes-cards-grid > div { 
        width: 100% !important; 
        max-width: 340px !important;
        transform: none !important;
        height: auto !important;
        min-height: auto !important;
        padding: 24px !important;
      }
      .portes-cards-grid > div p {
        height: auto !important;
      }
      .portes-cards-grid > div h3 {
        height: auto !important;
      }
      /* Cacher la card centrale et les flèches sur mobile */
      .portes-center-card {
        display: none !important;
      }
      /* Garder les side cards visibles et les empiler */
      .portes-side-card {
        width: 100% !important;
        max-width: 340px !important;
        height: auto !important;
      }
    }
    
    /* Mobile sections padding */
    @media (max-width: 768px) {
      .boutique-section {
        padding: 60px 16px !important;
      }
    }
    
    /* === OPTIMISATIONS MOBILE GLOBALES === */
    @media (max-width: 480px) {
      /* Supprimer l'espace blanc au-dessus du hero */
      .boutique-root main {
        margin-top: -52px !important;
        padding-top: 0 !important;
      }
      
      /* Hero mobile - colle directement sous le header */
      #hero {
        min-height: auto !important;
        padding-top: 10px !important;
        padding-bottom: 30px !important;
        margin-top: 0 !important;
      }
      
      /* Toutes les sections - réduire padding */
      section {
        padding: 40px 16px !important;
      }
      
      /* Grilles principales */
      .hero-grid-3,
      .biz-grid,
      .equipe-grid,
      .acquisition-grid,
      .install-grid,
      .domaine-cards,
      .offres-grid,
      .service-grid {
        grid-template-columns: 1fr !important;
        gap: 16px !important;
      }
      
      /* Cards - supprimer hauteurs fixes */
      .portes-cards-grid > div,
      .biz-card,
      .equipe-card,
      .acquisition-card,
      .domaine-card,
      .offres-card,
      .service-card {
        height: auto !important;
        min-height: auto !important;
        padding: 20px !important;
      }
      
      /* Card centrale */
      .portes-center-card {
        width: 100% !important;
        max-width: 320px !important;
        padding: 24px !important;
      }
      
      /* Titres plus petits */
      h1, h2 {
        font-size: clamp(24px, 7vw, 36px) !important;
        line-height: 1.2 !important;
      }
      
      h3 {
        font-size: clamp(18px, 5vw, 24px) !important;
      }
      
      /* Conteneurs */
      .boutique-container {
        padding: 0 16px !important;
      }
      
      /* Footers de section */
      .biz-footer,
      .equipe-footer,
      .domaine-bottom-banner,
      .acquisition-footer-v2 {
        grid-template-columns: 1fr !important;
        padding: 20px !important;
        gap: 12px !important;
      }
      
      .biz-footer-main,
      .equipe-footer-main,
      .acquisition-footer-main-v2 {
        flex-direction: column !important;
        text-align: center !important;
        align-items: center !important;
        border-right: none !important;
        border-bottom: 1px solid #f3e8d9 !important;
        padding-bottom: 16px !important;
        padding-right: 0 !important;
      }
      
      /* Installation section */
      .install-steps {
        flex-direction: column !important;
        gap: 16px !important;
      }
      .install-step {
        max-width: 100% !important;
        width: 100% !important;
      }
      .install-banner {
        flex-direction: column !important;
        text-align: center !important;
        padding: 20px !important;
      }
      .install-proofs {
        grid-template-columns: 1fr !important;
        gap: 12px !important;
      }
    }
    
    @media (max-width: 768px) {
      .portes-center-card {
        width: 100% !important;
        max-width: 340px !important;
        padding: 28px !important;
      }
    }
    
    /* Hover effect for side cards */
    .portes-card-hover:hover {
      transform: translateY(-6px);
      box-shadow: 0 12px 40px rgba(30, 58, 95, 0.12) !important;
    }
  `}</style>
);

// ═══════════════════════════════════════════════════════════════════════════════
// DONNÉES HERO
// ═══════════════════════════════════════════════════════════════════════════════
const HERO_FEATURES = [
  { icon: '🏪', title: 'Ta boutique', desc: 'Vitrine, produits, prestations, paiement en ligne.' },
  { icon: '🌐', title: 'Ton site internet', desc: 'Ton domaine, ton design, ton identité.' },
  { icon: '📱', title: 'Ton réseau social', desc: 'Feed, vidéos, salons, communauté engagée.' },
  { icon: '🤖', title: 'Ton équipe IA', desc: 'Assistant + jusqu\'à 12 agents qui travaillent pour toi.' },
  { icon: '💳', title: 'Tes paiements', desc: 'Stripe ou ton module propre. 0% de commission.' },
  { icon: '📝', title: 'Ton blog', desc: 'Articles, actualités, conseils pour tes clients.' },
];

const HERO_STATS = [
  { value: '2', label: 'Portes d\'entrée' },
  { value: '0%', label: 'Commission' },
  { value: '24/24', label: 'Assistant IA' },
  { value: '7', label: 'Jours d\'installation' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1 — HERO (Style identique à EquipeAgentsIA avec Phone Mockup)
// ═══════════════════════════════════════════════════════════════════════════════
const HeroSection = () => (
  <section id="hero" style={{ 
    background: `
      radial-gradient(ellipse 80% 60% at 65% 40%, rgba(29,78,137,0.5) 0%, transparent 55%),
      radial-gradient(circle at 20% 80%, rgba(255,122,0,0.08) 0%, transparent 40%),
      linear-gradient(165deg, #072B57 0%, #103A6D 35%, #1D4E89 70%, #072B57 100%)
    `, 
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative', 
    overflow: 'hidden',
    padding: 'clamp(40px, 6vw, 100px) clamp(20px, 5vw, 40px) clamp(40px, 5vw, 70px)', // Réduit padding-top mobile
  }}>
    {/* Glow orange subtil */}
    <div style={{ 
      position: 'absolute', 
      right: '5%', 
      top: '55%', 
      transform: 'translateY(-50%)',
      width: '700px',
      height: '700px',
      background: 'radial-gradient(circle, rgba(249, 115, 22, 0.10) 0%, rgba(249, 115, 22, 0.04) 35%, rgba(249, 115, 22, 0.02) 55%, transparent 75%)',
      filter: 'blur(100px)',
      zIndex: 0,
      pointerEvents: 'none'
    }} />
    
    {/* Texture légère */}
    <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E\")",
      opacity: 0.03,
      zIndex: 0,
      pointerEvents: 'none'
    }} />
    
    <div className="boutique-container" style={{ position: 'relative', zIndex: 1 }}>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: '1.5fr 0.55fr 0.75fr', 
        gap: 'clamp(20px, 2.5vw, 40px)', 
        alignItems: 'start' 
      }} className="hero-grid-3">
        
        {/* ═══ COLONNE GAUCHE — Texte principal ═══ */}
        <div>
          {/* Badge - masqué sur mobile car affiché au-dessus du téléphone */}
          <motion.div
            {...fadeUpHero(STAGGER.title)}
            className="hide-on-mobile"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 999,
              background: 'rgba(249, 115, 22, 0.15)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              color: C.orange,
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 28,
              willChange: 'transform, opacity',
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.orange }} />
            Zéro commission, zéro intermédiaire.
          </motion.div>

          {/* Titre - masqué sur mobile car affiché au-dessus du téléphone */}
          <motion.h1
            {...fadeUpHero(STAGGER.subtitle)}
            className="hide-on-mobile"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(36px, 4.5vw, 64px)',
              lineHeight: 1.1,
              color: C.white,
              marginBottom: 12,
              willChange: 'transform, opacity',
            }}
          >
            Ta boutique virtuelle.
          </motion.h1>
          
          {/* Sous-titre accrocheur - masqué sur mobile */}
          <motion.p
            {...fadeUpHero(STAGGER.text)}
            className="hide-on-mobile"
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(22px, 2.8vw, 36px)',
              color: C.orange,
              marginBottom: 24,
              willChange: 'transform, opacity',
            }}
          >
            Tu vends, tu encaisses.
          </motion.p>
          
          {/* Trait orange décoratif - masqué sur mobile */}
          <motion.div
            {...fadeUpHero(STAGGER.subtitle)}
            className="hide-on-mobile"
            style={{
              width: 60,
              height: 4,
              background: C.orange,
              borderRadius: 2,
              marginBottom: 24,
            }}
          />

          {/* Sous-titre - masqué sur mobile */}
          <motion.div
            {...fadeUpHero(STAGGER.buttons)}
            className="hide-on-mobile"
            style={{
              fontSize: 'clamp(15px, 1.8vw, 18px)',
              lineHeight: 1.6,
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: 24,
              maxWidth: 520,
              willChange: 'transform, opacity',
            }}
          >
            <p style={{ marginBottom: 8, color: 'rgba(255, 255, 255, 0.8)' }}>
              Ta boutique virtuelle aménagée en 7 jours dans le 1<sup>er</sup> centre commercial du bâtiment.
            </p>
            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.8)' }}>
              2 portes d'entrée, 1 seul endroit : <span style={{ color: C.orange, fontWeight: 600 }}>chez toi.</span>
            </p>
          </motion.div>

          {/* Prix - masqué sur mobile */}
          <motion.div
            {...fadeUpHero(STAGGER.image)}
            className="hide-on-mobile"
            style={{ marginBottom: 8, willChange: 'transform, opacity' }}
          >
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16 }}>À partir de </span>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 42, color: C.white }}>50€</span>
            <span style={{ color: C.orange, fontSize: 16, fontWeight: 600 }}>*</span>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16 }}> / Mois</span>
          </motion.div>
          <motion.p
            {...fadeUpHero(STAGGER.extra)}
            className="hide-on-mobile"
            style={{ 
              fontSize: 12, 
              color: 'rgba(255,255,255,0.5)', 
              marginBottom: 28,
              fontStyle: 'italic',
              willChange: 'transform, opacity',
            }}
          >
            *Prix mensuel pour un Abonnement Annuel
          </motion.p>

          {/* CTAs - masqués sur mobile */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: DURATION, ease: EASE, delay: STAGGER.extra + 0.05 }}
            className="hide-on-mobile"
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 40, willChange: 'transform, opacity' }}
          >
            <a
              href="#tarifs"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                color: '#fff',
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 14,
                textDecoration: 'none',
                boxShadow: '0 8px 20px rgba(34,197,94,0.4)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              onMouseOver={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 12px 28px rgba(34,197,94,0.5)'; }}
              onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 8px 20px rgba(34,197,94,0.4)'; }}
            >
              Voir les offres fondateurs →
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ display: 'flex', gap: 0 }}
            className="hero-stats hide-on-mobile"
          >
            {HERO_STATS.map((stat, i) => (
              <div 
                key={i} 
                style={{ 
                  textAlign: 'center',
                  padding: '0 20px',
                  borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.15)' : 'none',
                }}
              >
                <div style={{ 
                  fontFamily: "'Plus Jakarta Sans', sans-serif", 
                  fontWeight: 800, 
                  fontSize: 28, 
                  color: C.orange 
                }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ═══ COLONNE CENTRALE — Liste de features ═══ */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="hero-features-col"
          style={{ paddingTop: 60 }}
        >
          <h3 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            fontSize: 20,
            color: C.orange,
            marginBottom: 28,
          }}>
            Bienvenue chez toi.
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {HERO_FEATURES.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  flexShrink: 0,
                }}>
                  {f.icon}
                </div>
                <div>
                  <div style={{ 
                    fontWeight: 700, 
                    fontSize: 15, 
                    color: C.white,
                    marginBottom: 3,
                  }}>
                    {f.title}
                  </div>
                  <div style={{ 
                    fontSize: 13, 
                    color: 'rgba(255,255,255,0.55)',
                    lineHeight: 1.5,
                  }}>
                    {f.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* ═══ COLONNE DROITE — Phone Mockup Image ═══ */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center',
            alignSelf: 'center',
            width: '100%',
          }}
          className="hero-phone-col"
        >
          {/* Titre mobile uniquement - au-dessus du téléphone */}
          <div 
            className="show-on-mobile-only"
            style={{
              textAlign: 'center',
              marginBottom: 16,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <h1 style={{
              fontSize: 23,
              fontWeight: 800,
              color: '#fff',
              lineHeight: 1.2,
              marginBottom: 12,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}>
              Loue ta boutique virtuelle
            </h1>
            <p style={{
              fontSize: 15,
              fontWeight: 700,
              color: C.orange,
              lineHeight: 1.3,
              textAlign: 'center',
              whiteSpace: 'nowrap',
              marginBottom: 14,
            }}>
              0% de commission, sans intermédiaire
            </p>
            {/* Badge Offre Fondateur */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 18px',
              background: 'rgba(34,197,94,0.15)',
              border: '1px solid rgba(34,197,94,0.4)',
              borderRadius: 50,
            }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#4ade80' }}>
                👑 Offre Fondateur -
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                dès 25€/mois à vie
              </span>
            </div>
          </div>
          
          <img 
            src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/d8o1jy5w_15643.png"
            alt="Aperçu boutique mobile"
            style={{
              maxWidth: 480,
              width: '100%',
              height: 'auto',
              filter: 'drop-shadow(0 40px 80px rgba(0,0,0,0.25))',
            }}
          />
          
          {/* Texte explicatif mobile */}
          <p 
            className="show-on-mobile-only"
            style={{
              fontSize: 13,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
              lineHeight: 1.6,
              marginTop: 16,
              padding: '0 10px',
              marginBottom: 12,
            }}
          >
            Tes clients entrent dans ta boutique virtuelle comme dans un vrai centre commercial. Ils découvrent ton univers et achètent directement chez toi.
          </p>
          
          {/* Bouton mobile */}
          <a
            href="#tarifs"
            className="show-on-mobile-only"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: '#fff',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 14,
              textDecoration: 'none',
              boxShadow: '0 8px 20px rgba(34,197,94,0.4)',
              marginBottom: 16,
            }}
          >
            Voir les offres fondateurs →
          </a>
          
          {/* Mini badges mobile - sous le bouton - IDENTIQUE landing page */}
          <div 
            className="show-on-mobile-only"
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 6,
              flexWrap: 'nowrap',
              marginTop: 4,
            }}
          >
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 6,
              padding: '5px 8px',
              fontSize: 10,
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.85)',
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span>100% chez toi</span>
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 6,
              padding: '5px 8px',
              fontSize: 10,
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.85)',
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
              </svg>
              <span>Cockpit inclus</span>
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 6,
              padding: '5px 8px',
              fontSize: 10,
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.85)',
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              <span>IA intégrée</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
    
    {/* Note prix en bas à droite - Mobile uniquement */}
    <div 
      className="show-on-mobile-only"
      style={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        textAlign: 'right',
        zIndex: 10,
      }}
    >
      <div style={{ 
        fontSize: 9, 
        color: 'rgba(255,255,255,0.5)',
        fontStyle: 'italic',
      }}>
        *Prix mensuel pour un abonnement annuel
      </div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION INTERMÉDIAIRE — BIEN PLUS QU'UN SITE INTERNET
// ═══════════════════════════════════════════════════════════════════════════════
const BoutiqueExplicationSection = () => (
  <section 
    style={{ 
      background: C.cream,
      padding: '80px 20px', 
      fontFamily: "'DM Sans', sans-serif", 
      color: C.navyDark, 
      textAlign: 'center',
    }}
  >
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{ 
          marginBottom: 20, 
          display: 'inline-block', 
          padding: '8px 18px', 
          background: 'rgba(232, 101, 10, 0.08)',
          border: `1px solid ${C.peachBorder}`, 
          borderRadius: 999, 
          fontSize: 12,
          fontWeight: 600,
          color: C.orangeDark,
          letterSpacing: '0.5px',
        }}
      >
        💡 BIEN PLUS QU'UN SITE INTERNET
      </motion.div>

      {/* Titre */}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        style={{ 
          fontFamily: "'Plus Jakarta Sans', sans-serif", 
          fontWeight: 800,
          fontSize: 'clamp(28px, 5vw, 44px)',
          lineHeight: 1.2, 
          marginBottom: 24,
          color: C.navyDark,
        }}
      >
        Une vraie <span style={{ color: C.orangeDark }}>boutique virtuelle</span>
      </motion.h2>

      {/* Texte explicatif */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.15 }}
        style={{
          fontSize: 'clamp(15px, 2vw, 18px)',
          lineHeight: 1.7,
          color: 'rgba(29, 41, 57, 0.75)',
          maxWidth: 750,
          margin: '0 auto 40px',
        }}
      >
        Chez AbracadaWorld, tu n'as pas un simple site internet. Tu possèdes une <strong style={{ color: C.navyDark }}>véritable boutique</strong> dans un centre commercial virtuel — avec ton nom, ta marque, ton identité.
      </motion.p>

      {/* Features Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 20,
          maxWidth: 800,
          margin: '0 auto',
        }}
      >
        {/* Feature 1 - Cockpit */}
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: '24px 20px',
          boxShadow: '0 4px 20px rgba(29, 41, 57, 0.06)',
          border: `1px solid ${C.peachBorder}`,
          textAlign: 'left',
        }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'rgba(59,130,246,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <path d="M3 9h18M9 21V9"/>
            </svg>
          </div>
          <h4 style={{ fontWeight: 700, fontSize: 16, color: C.navyDark, marginBottom: 6 }}>
            Ton Cockpit
          </h4>
          <p style={{ fontSize: 13, color: 'rgba(29,41,57,0.65)', lineHeight: 1.5 }}>
            Ton logiciel de gestion complet intégré. CRM, devis, factures, planning — tout centralisé.
          </p>
        </div>

        {/* Feature 2 - Équipe IA */}
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: '24px 20px',
          boxShadow: '0 4px 20px rgba(29, 41, 57, 0.06)',
          border: `1px solid ${C.peachBorder}`,
          textAlign: 'left',
        }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'rgba(249,115,22,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </div>
          <h4 style={{ fontWeight: 700, fontSize: 16, color: C.navyDark, marginBottom: 6 }}>
            Tes salariés IA
          </h4>
          <p style={{ fontSize: 13, color: 'rgba(29,41,57,0.65)', lineHeight: 1.5 }}>
            Commercial, administratif, community manager... Une équipe IA qui travaille pour toi 24/24.
          </p>
        </div>

        {/* Feature 3 - Vendeur */}
        <div style={{
          background: 'white',
          borderRadius: 16,
          padding: '24px 20px',
          boxShadow: '0 4px 20px rgba(29, 41, 57, 0.06)',
          border: `1px solid ${C.peachBorder}`,
          textAlign: 'left',
        }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'rgba(34,197,94,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <h4 style={{ fontWeight: 700, fontSize: 16, color: C.navyDark, marginBottom: 6 }}>
            Ton vendeur virtuel
          </h4>
          <p style={{ fontSize: 13, color: 'rgba(29,41,57,0.65)', lineHeight: 1.5 }}>
            Accueille tes visiteurs, répond aux questions, qualifie les prospects — même quand tu dors.
          </p>
        </div>
      </motion.div>

      {/* Phrase finale */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        style={{
          marginTop: 40,
          padding: '20px 30px',
          background: `linear-gradient(135deg, ${C.navy} 0%, #1a2744 100%)`,
          borderRadius: 16,
          maxWidth: 600,
          margin: '40px auto 0',
        }}
      >
        <p style={{
          fontSize: 'clamp(15px, 2vw, 17px)',
          fontWeight: 600,
          color: 'white',
          lineHeight: 1.6,
          margin: 0,
        }}>
          Tu encaisses <span style={{ color: '#4ade80' }}>sans aucune commission</span>. Tu vends <span style={{ color: C.orange }}>sans intermédiaire</span>.
        </p>
      </motion.div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2 — LES 2 PORTES D'ENTRÉE (Design Premium Optimisé)
// ═══════════════════════════════════════════════════════════════════════════════
const PortesSection = () => (
  <section 
    id="concept" 
    style={{ 
      background: `linear-gradient(180deg, ${C.cream} 0%, #fef6f0 50%, ${C.cream} 100%)`,
      padding: '100px 20px', 
      fontFamily: "'DM Sans', sans-serif", 
      color: C.navyDark, 
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* CSS pour animations des flèches */}
    <style>{`
      @keyframes arrowFlowRight {
        0% { transform: translateX(-8px); opacity: 0.25; }
        50% { transform: translateX(0px); opacity: 0.6; }
        100% { transform: translateX(-8px); opacity: 0.25; }
      }
      @keyframes arrowFlowLeft {
        0% { transform: translateX(8px); opacity: 0.25; }
        50% { transform: translateX(0px); opacity: 0.6; }
        100% { transform: translateX(8px); opacity: 0.25; }
      }
      .arrow-flow-right { animation: arrowFlowRight 2.5s ease-in-out infinite; }
      .arrow-flow-left { animation: arrowFlowLeft 2.5s ease-in-out infinite; }
      @media (max-width: 1024px) {
        .portes-arrow { display: none !important; }
      }
    `}</style>

    {/* Subtle decorative elements */}
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <div style={{ 
        position: 'absolute', 
        top: '-5%', 
        right: '-5%', 
        width: 400, 
        height: 400, 
        borderRadius: '50%', 
        background: `radial-gradient(circle, ${C.peachBorder}40 0%, transparent 70%)`,
      }} />
      <div style={{ 
        position: 'absolute', 
        bottom: '-10%', 
        left: '-5%', 
        width: 300, 
        height: 300, 
        borderRadius: '50%', 
        background: `radial-gradient(circle, ${C.peachBorder}30 0%, transparent 70%)`,
      }} />
    </div>

    <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{ 
          marginBottom: 16, 
          display: 'inline-block', 
          padding: '8px 18px', 
          background: 'rgba(232, 101, 10, 0.08)',
          border: `1px solid ${C.peachBorder}`, 
          borderRadius: 999, 
          fontSize: 12,
          fontWeight: 600,
          color: C.orangeDark,
          letterSpacing: '0.5px',
        }}
      >
        🏬 TON BUSINESS, TON INDÉPENDANCE
      </motion.div>

      {/* Titre Premium */}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        style={{ 
          fontFamily: "'Plus Jakarta Sans', sans-serif", 
          fontWeight: 800,
          lineHeight: 1.15, 
          margin: '20px 0',
          color: C.navyDark,
        }}
      >
        <span style={{ 
          fontSize: 'clamp(26px, 4.5vw, 40px)',
          display: 'block',
          marginBottom: 8,
        }}>Deux entrées. Une seule destination :</span>
        <span style={{ 
          display: 'inline-flex',
          alignItems: 'center',
          gap: 16,
        }}>
          <span style={{
            width: 'clamp(30px, 8vw, 60px)',
            height: 2,
            background: `linear-gradient(90deg, transparent 0%, ${C.orangeDark} 100%)`,
            borderRadius: 2,
          }} />
          <span style={{ 
            color: C.orangeDark,
            fontSize: 'clamp(38px, 6.5vw, 58px)',
            textShadow: '0 4px 30px rgba(232, 101, 10, 0.3)',
            fontStyle: 'italic',
            fontWeight: 800,
          }}>chez toi.</span>
          <span style={{
            width: 'clamp(30px, 8vw, 60px)',
            height: 2,
            background: `linear-gradient(90deg, ${C.orangeDark} 0%, transparent 100%)`,
            borderRadius: 2,
          }} />
        </span>
      </motion.h2>

      {/* Sous-titre */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        style={{ 
          maxWidth: 700, 
          margin: '0 auto 60px', 
          color: '#64748b',
          fontSize: 17,
          lineHeight: 1.6,
        }}
      >
        Tes clients arrivent par où ils veulent. Ils arrivent toujours chez toi.
      </motion.p>

      {/* 3 Cards avec flèches */}
      <div 
        className="portes-cards-grid"
        style={{ 
          display: 'flex', 
          gap: 16, 
          alignItems: 'center', 
          justifyContent: 'center', 
          flexWrap: 'wrap' 
        }}
      >
        {/* LEFT CARD — Via la marketplace */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          style={{ 
            background: C.white, 
            color: C.navyDark, 
            border: `2px solid ${C.navy}`, 
            borderRadius: 20, 
            padding: 32, 
            width: 280,
            height: 360,
            textAlign: 'center',
            boxShadow: '0 8px 40px rgba(30, 58, 95, 0.12)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          className="portes-card-hover portes-side-card"
        >
          {/* Icon outline - hauteur fixe */}
          <div style={{ 
            width: 64, 
            height: 64, 
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 16,
            background: `linear-gradient(135deg, ${C.orangeDark}10 0%, ${C.orangeDark}05 100%)`,
            border: `1.5px solid ${C.orangeDark}25`,
            flexShrink: 0,
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          {/* Titre - hauteur fixe */}
          <h3 style={{ fontSize: 18, marginBottom: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, color: C.navyDark, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            Via la marketplace
          </h3>
          {/* Description - hauteur fixe */}
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, marginBottom: 20, height: 72, display: 'flex', alignItems: 'center', textAlign: 'center', flexShrink: 0 }}>
            Tes clients te découvrent dans le centre commercial AbracadaBati. Ils entrent dans ta boutique.
          </p>
          {/* Badge - en bas */}
          <div style={{ 
            display: 'inline-block', 
            background: `${C.orangeDark}15`, 
            color: C.orangeDark, 
            padding: '10px 20px', 
            borderRadius: 999, 
            fontSize: 12,
            fontWeight: 700,
            marginTop: 'auto',
          }}>
            Trafic qualifié
          </div>
        </motion.div>

        {/* ARROW LEFT → CENTER */}
        <div className="arrow-flow-right portes-arrow" style={{ padding: '0 6px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </div>

        {/* CENTER CARD — Ta boutique (+10% bigger) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="portes-center-card"
          style={{ 
            background: `linear-gradient(160deg, #1a2a4a 0%, #243b6b 100%)`, 
            border: `3px solid ${C.orangeDark}`, 
            borderRadius: 24, 
            padding: 44, 
            width: 352, 
            boxShadow: `0 0 80px rgba(232, 101, 10, 0.18), 0 24px 60px rgba(30, 58, 95, 0.2)`,
            textAlign: 'center',
          }}
        >
          {/* Icon outline */}
          <div style={{ 
            width: 72, 
            height: 72, 
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 18,
            background: 'rgba(232, 101, 10, 0.12)',
            border: '1.5px solid rgba(232, 101, 10, 0.3)',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          </div>
          <h3 style={{ fontSize: 26, marginBottom: 24, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, color: C.white }}>
            Ta boutique
          </h3>

          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', fontSize: 16, lineHeight: 2.2, textAlign: 'left', color: 'rgba(255, 248, 244, 0.9)' }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: C.orangeDark, fontSize: 18, fontWeight: 700 }}>✓</span> Tes clients sont chez toi
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: C.orangeDark, fontSize: 18, fontWeight: 700 }}>✓</span> Tu vends
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ color: C.orangeDark, fontSize: 18, fontWeight: 700 }}>✓</span> Tu encaisses directement
            </li>
          </ul>

          <div style={{ 
            fontSize: 24, 
            color: C.orangeDark, 
            fontWeight: 800, 
            marginBottom: 10,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            letterSpacing: '0.8px',
          }}>
            0% commission. Pour toujours.
          </div>

          <div style={{ fontSize: 13, color: 'rgba(255, 248, 244, 0.5)' }}>
            Ton business. Tes règles.
          </div>
        </motion.div>

        {/* ARROW CENTER ← RIGHT */}
        <div className="arrow-flow-left portes-arrow" style={{ padding: '0 6px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5"/><path d="m12 19-7-7 7-7"/>
          </svg>
        </div>

        {/* RIGHT CARD — Par ton site */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          style={{ 
            background: C.white, 
            color: C.navyDark,
            border: `2px solid ${C.navy}`, 
            borderRadius: 20, 
            padding: 32, 
            width: 280,
            height: 360,
            textAlign: 'center',
            boxShadow: '0 8px 40px rgba(30, 58, 95, 0.12)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
          className="portes-card-hover portes-side-card"
        >
          {/* Icon outline - hauteur fixe */}
          <div style={{ 
            width: 64, 
            height: 64, 
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 16,
            background: `linear-gradient(135deg, ${C.navy}10 0%, ${C.navy}05 100%)`,
            border: `1.5px solid ${C.navy}20`,
            flexShrink: 0,
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.navy} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
          {/* Titre - hauteur fixe */}
          <h3 style={{ fontSize: 18, marginBottom: 12, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, color: C.navyDark, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            Par ton site
          </h3>
          {/* Description - hauteur fixe */}
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7, marginBottom: 20, height: 72, display: 'flex', alignItems: 'center', textAlign: 'center', flexShrink: 0 }}>
            Tes clients arrivent directement chez toi. Ils ne voient que toi. Ils paient chez toi.
          </p>
          {/* Badge - en bas */}
          <div style={{ 
            display: 'inline-block', 
            background: `${C.navy}12`,
            border: `1px solid ${C.navy}20`,
            color: C.navyDark,
            padding: '10px 20px', 
            borderRadius: 999, 
            fontSize: 12,
            fontWeight: 700,
            marginTop: 'auto',
          }}>
            Ton identité
          </div>
        </motion.div>
      </div>

      {/* Texte final */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        style={{ marginTop: 64, fontSize: 17, color: C.navyDark }}
      >
        Peu importe d'où viennent tes clients…<br />
        <span style={{ color: C.orangeDark, fontWeight: 700 }}>ils arrivent toujours chez toi.</span>
      </motion.div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3 — BUSINESS COMPLET (Pixel Perfect)
// ═══════════════════════════════════════════════════════════════════════════════
const BUSINESS_CARDS_V2 = [
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
    title: 'Tes clients\nte trouvent', 
    desc: 'Sur Google, sur la plateforme, partout.', 
    result: 'Plus de visibilité. Plus d\'opportunités.', 
    highlight: false 
  },
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
      </svg>
    ),
    title: 'Tu vends\ndirectement', 
    desc: 'Produits ou prestations, sans intermédiaire.', 
    result: 'Plus de ventes. Plus de liberté.', 
    highlight: false 
  },
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    ),
    title: 'Tu encaisses\nsans friction', 
    desc: 'L\'argent arrive directement sur ton compte.', 
    result: 'Simple. Rapide. Direct.', 
    highlight: true 
  },
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    title: 'Ils te choisissent\navant même de te parler', 
    desc: 'Avis, contenu, présence.', 
    result: 'Plus de confiance. Plus de clients.', 
    highlight: false 
  },
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    title: 'Tout est organisé\npour toi', 
    desc: 'Rendez-vous, clients, demandes.', 
    result: 'Moins de bordel. Plus de contrôle.', 
    highlight: false 
  },
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      </svg>
    ),
    title: 'Tu montres ton\nsavoir-faire', 
    desc: 'Réalisations, conseils, contenus.', 
    result: 'Plus d\'autorité. Plus d\'impact.', 
    highlight: false 
  },
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
    title: 'Une partie du travail\nse fait pour toi', 
    desc: 'Réponses, contenu, organisation.', 
    result: 'Plus de temps. Plus d\'efficacité.', 
    highlight: true 
  },
  { 
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#E8650A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
      </svg>
    ),
    title: 'Tu pilotes tout\nen temps réel', 
    desc: 'Clients, ventes, performances.', 
    result: 'Tu prends les bonnes décisions.', 
    highlight: false 
  },
];

const FeaturesSection = () => (
  <section id="features" style={{ 
    padding: '80px 20px',
    background: C.cream,
    textAlign: 'center',
  }}>
    {/* Styles CSS */}
    <style>{`
      .biz-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 20px;
        max-width: 1200px;
        margin: 0 auto;
      }
      .biz-card {
        background: #fff;
        border: 1px solid ${C.peachBorder};
        border-radius: 20px;
        padding: 24px;
        text-align: left;
        box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        display: flex;
        flex-direction: column;
        transition: transform 0.25s ease, box-shadow 0.25s ease;
        position: relative;
      }
      .biz-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 15px 40px rgba(0,0,0,0.08);
      }
      .biz-card.highlight {
        border: 2px solid ${C.orangeDark};
        box-shadow: 0 15px 40px rgba(232,101,10,0.15);
      }
      .biz-footer {
        max-width: 1200px;
        margin: 40px auto 0;
        padding: 28px 32px;
        border-radius: 20px;
        background: #fff;
        border: 1px solid ${C.peachBorder};
        box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        display: grid;
        grid-template-columns: 1.4fr repeat(4, 1fr);
        align-items: center;
        gap: 24px;
      }
      .biz-footer-main {
        display: flex;
        align-items: center;
        gap: 16px;
        padding-right: 24px;
        border-right: 1px solid ${C.peachBorder};
      }
      @media (max-width: 1100px) {
        .biz-grid { grid-template-columns: repeat(2, 1fr); }
        .biz-footer { grid-template-columns: 1fr 1fr; }
        .biz-footer-main { 
          grid-column: 1 / -1; 
          border-right: none; 
          border-bottom: 1px solid ${C.peachBorder}; 
          padding-bottom: 20px; 
          padding-right: 0;
        }
      }
      @media (max-width: 640px) {
        .biz-grid { grid-template-columns: 1fr; gap: 16px; }
        .biz-card { padding: 20px; }
        .biz-footer { grid-template-columns: 1fr; padding: 24px 20px; gap: 16px; }
        .biz-footer-main { flex-direction: column; text-align: center; align-items: center; }
      }
    `}</style>

    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{
          display: 'inline-block',
          padding: '8px 16px',
          borderRadius: 999,
          background: C.peachBorder,
          color: C.orangeDark,
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 20,
        }}
      >
        🚀 Ta boutique complète
      </motion.div>

      {/* Titre */}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        style={{
          margin: 0,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 800,
          lineHeight: 1.1,
          color: C.navyDark,
        }}
      >
        Pas juste un site.<br />
        <span style={{ color: C.orangeDark }}>Un business qui tourne pour toi.</span>
      </motion.h2>

      {/* Sous-titre */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        style={{
          marginTop: 20,
          fontSize: 'clamp(16px, 2vw, 18px)',
          color: 'rgba(26, 42, 74, 0.7)',
          lineHeight: 1.6,
        }}
      >
        En 7 jours, tout est en place.<br />
        Tu arrives, tes clients peuvent déjà te trouver, réserver, acheter et te payer.
      </motion.p>

      {/* Grille de cartes */}
      <div className="biz-grid" style={{ marginTop: 50 }}>
        {BUSINESS_CARDS_V2.map((card, i) => (
          <motion.article
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className={`biz-card ${card.highlight ? 'highlight' : ''}`}
          >
            {/* Badge Inclus */}
            {card.highlight && (
              <div style={{
                position: 'absolute',
                top: -10,
                right: 16,
                background: C.orangeDark,
                color: '#fff',
                padding: '4px 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
              }}>
                Inclus
              </div>
            )}

            {/* Header avec icône + titre */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                background: C.cream,
                border: `1px solid ${C.peachBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {card.icon}
              </div>
              <h3 style={{
                margin: 0,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 17,
                fontWeight: 700,
                lineHeight: 1.25,
                color: C.navyDark,
                whiteSpace: 'pre-line',
              }}>
                {card.title}
              </h3>
            </div>

            {/* Description */}
            <p style={{
              margin: '0 0 16px',
              fontSize: 14,
              color: 'rgba(26, 42, 74, 0.7)',
              lineHeight: 1.5,
              flex: 1,
            }}>
              {card.desc}
            </p>

            {/* Résultat */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              color: C.orangeDark,
              fontSize: 14,
              fontWeight: 700,
              lineHeight: 1.35,
              marginTop: 'auto',
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: C.orangeDark,
                color: '#fff',
                fontSize: 10,
                fontWeight: 800,
                flexShrink: 0,
                marginTop: 2,
              }}>✓</span>
              {card.result}
            </div>
          </motion.article>
        ))}
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="biz-footer"
      >
        {/* Main */}
        <div className="biz-footer-main">
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: C.navyDark,
            color: C.cream,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            flexShrink: 0,
          }}>★</div>
          <div style={{ textAlign: 'left' }}>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(18px, 2vw, 22px)',
              fontWeight: 800,
              color: C.navyDark,
              lineHeight: 1.3,
            }}>
              Tout est déjà connecté.
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(18px, 2vw, 22px)',
              fontWeight: 800,
              color: C.orangeDark,
              lineHeight: 1.3,
            }}>
              Tu n'as rien à construire.
            </div>
          </div>
        </div>

        {/* Preuves */}
        {[
          { icon: '✓', label: 'Sécurisé\net fiable' },
          { icon: '🎧', label: 'Support humain\nréactif' },
          { icon: '☁️', label: 'Sauvegardes\nautomatiques' },
          { icon: '🔒', label: 'Données\nprotégées' },
        ].map((proof, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 14,
            fontWeight: 600,
            color: C.navyDark,
            lineHeight: 1.35,
          }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: C.cream,
              border: `1px solid ${C.peachBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              flexShrink: 0,
            }}>
              {proof.icon}
            </div>
            <div style={{ whiteSpace: 'pre-line' }}>{proof.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Note finale */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        style={{
          marginTop: 24,
          fontSize: 15,
          color: 'rgba(26, 42, 74, 0.6)',
          fontWeight: 500,
        }}
      >
        Une solution complète, pensée pour les pros du bâtiment.
      </motion.p>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 4 — TON ÉQUIPE IA (Nouveau design pixel perfect)
// ═══════════════════════════════════════════════════════════════════════════════
const EquipeSection = () => (
  <section id="equipe-ia" style={{ 
    padding: '80px 20px',
    background: C.cream,
    textAlign: 'center',
  }}>
    {/* CSS Responsive */}
    <style>{`
      .equipe-grid {
        display: grid;
        grid-template-columns: 1fr 1.15fr 1fr;
        gap: 24px;
        max-width: 1200px;
        margin: 0 auto;
        align-items: stretch;
      }
      .equipe-card {
        background: #fff;
        border: 1px solid ${C.peachBorder};
        border-radius: 20px;
        padding: 28px;
        text-align: left;
        box-shadow: 0 10px 30px rgba(0,0,0,0.04);
        display: flex;
        flex-direction: column;
      }
      .equipe-card.highlight {
        border: 2px solid ${C.orangeDark};
        box-shadow: 0 15px 45px rgba(232,101,10,0.12);
      }
      .equipe-footer {
        max-width: 1200px;
        margin: 40px auto 0;
        padding: 28px 32px;
        border-radius: 20px;
        background: #fff;
        border: 1px solid ${C.peachBorder};
        box-shadow: 0 10px 30px rgba(0,0,0,0.04);
        display: grid;
        grid-template-columns: 1.5fr repeat(4, 1fr);
        align-items: center;
        gap: 20px;
      }
      .equipe-footer-main {
        display: flex;
        align-items: center;
        gap: 16px;
        padding-right: 24px;
        border-right: 1px solid ${C.peachBorder};
      }
      @media (max-width: 1100px) {
        .equipe-grid { grid-template-columns: 1fr; gap: 20px; }
        .equipe-card.highlight { order: -1; }
        .equipe-footer { grid-template-columns: 1fr 1fr; }
        .equipe-footer-main { 
          grid-column: 1 / -1; 
          border-right: none; 
          border-bottom: 1px solid ${C.peachBorder}; 
          padding-bottom: 20px; 
          padding-right: 0;
        }
      }
      @media (max-width: 640px) {
        .equipe-footer { grid-template-columns: 1fr; padding: 24px 20px; gap: 16px; }
        .equipe-footer-main { flex-direction: column; text-align: center; align-items: center; }
      }
    `}</style>

    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          borderRadius: 999,
          background: C.peachBorder,
          color: C.orangeDark,
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 20,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        Ton équipe IA
      </motion.div>

      {/* Titre */}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        style={{
          margin: 0,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 800,
          lineHeight: 1.1,
          color: C.navyDark,
        }}
      >
        Tu travailles.<br />
        <span style={{ color: C.orangeDark }}>Ton équipe IA travaille avec toi.</span>
      </motion.h2>

      {/* Sous-titre */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        style={{
          marginTop: 20,
          fontSize: 'clamp(15px, 2vw, 17px)',
          color: 'rgba(26, 42, 74, 0.7)',
          lineHeight: 1.6,
          maxWidth: 700,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        Ton Super Assistant pilote une équipe complète d'agents IA dédiée à ta boutique pour t'aider à vendre, organiser, répondre et suivre tes clients.
      </motion.p>

      {/* Grille 3 cartes */}
      <div className="equipe-grid" style={{ marginTop: 50 }}>
        {/* CARTE 1 — Super Assistant */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="equipe-card"
        >
          {/* Header icône + badge */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: C.cream,
              border: `1px solid ${C.peachBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.navyDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <circle cx="9" cy="10" r="1" fill={C.navyDark}/>
                <circle cx="15" cy="10" r="1" fill={C.navyDark}/>
              </svg>
            </div>
            <span style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: `1px solid ${C.peachBorder}`,
              background: '#fff',
              color: C.navyDark,
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}>
              Toutes<br />les formules
            </span>
          </div>

          {/* Titre */}
          <h3 style={{
            margin: '0 0 12px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 20,
            fontWeight: 800,
            lineHeight: 1.25,
            color: C.navyDark,
          }}>
            Ton Super Assistant<br />travaille pour toi
          </h3>

          {/* Description */}
          <p style={{
            margin: '0 0 20px',
            fontSize: 14,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
            flex: 1,
          }}>
            Il répond aux messages, gère les demandes, organise les informations et lance les actions.
          </p>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            color: C.orangeDark,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.35,
            marginTop: 'auto',
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              flexShrink: 0,
              marginTop: 1,
            }}>✓</span>
            Tu ne perds plus<br />aucun client.
          </div>
        </motion.article>

        {/* CARTE 2 — Centrale (highlight) */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="equipe-card highlight"
        >
          {/* Icône */}
          <div style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            background: C.cream,
            border: `1px solid ${C.peachBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>

          {/* Titre */}
          <h3 style={{
            margin: '0 0 12px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 22,
            fontWeight: 800,
            lineHeight: 1.25,
            color: C.navyDark,
          }}>
            Il pilote toute ton<br />équipe IA
          </h3>

          {/* Description */}
          <p style={{
            margin: '0 0 20px',
            fontSize: 14,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
          }}>
            Ton Super Assistant coordonne une équipe d'agents spécialisés pour chaque mission.
          </p>

          {/* Encart Business/Premium */}
          <div style={{
            background: C.cream,
            borderRadius: 14,
            padding: 16,
            marginBottom: 20,
          }}>
            {/* Badge */}
            <div style={{
              display: 'inline-block',
              padding: '5px 12px',
              borderRadius: 999,
              background: C.orangeDark,
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 14,
            }}>
              Inclus dans formule Business et Premium
            </div>

            {/* Business */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: '#fff',
                border: `1px solid ${C.peachBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.orangeDark }}>
                  Formule Business : +4 agents IA
                </div>
                <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>
                  Commercial · Administratif · Service client · Organisation
                </div>
              </div>
            </div>

            {/* Premium */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: '#fff',
                border: `1px solid ${C.peachBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.orangeDark }}>
                  Formule Premium : jusqu'à 12 agents IA
                </div>
                <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>
                  Dream Team complète
                </div>
              </div>
            </div>
          </div>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            color: C.orangeDark,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.35,
            marginTop: 'auto',
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              flexShrink: 0,
              marginTop: 1,
            }}>✓</span>
            Une équipe complète.<br />Sans recruter.
          </div>
        </motion.article>

        {/* CARTE 3 — Business continue */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="equipe-card"
        >
          {/* Icône */}
          <div style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: C.cream,
            border: `1px solid ${C.peachBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 20,
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
              <path d="M18 6l4 4" stroke={C.orangeDark} strokeWidth="2"/>
              <path d="M22 6l-4 4" stroke={C.orangeDark} strokeWidth="2"/>
            </svg>
          </div>

          {/* Titre */}
          <h3 style={{
            margin: '0 0 12px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 20,
            fontWeight: 800,
            lineHeight: 1.25,
            color: C.navyDark,
          }}>
            Ton business continue<br />d'avancer, même sans toi
          </h3>

          {/* Description */}
          <p style={{
            margin: '0 0 20px',
            fontSize: 14,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
            flex: 1,
          }}>
            Rendez-vous, relances, réponses, contenus, suivi client… tout continue à tourner 24h/24.
          </p>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            color: C.orangeDark,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.35,
            marginTop: 'auto',
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              fontSize: 11,
              fontWeight: 800,
              flexShrink: 0,
              marginTop: 1,
            }}>✓</span>
            Ton activité<br />tourne en continu.
          </div>
        </motion.article>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="equipe-footer"
      >
        {/* Main */}
        <div className="equipe-footer-main">
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: C.navyDark,
            color: C.cream,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            flexShrink: 0,
          }}>★</div>
          <div style={{ textAlign: 'left' }}>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(16px, 2vw, 20px)',
              fontWeight: 800,
              color: C.navyDark,
              lineHeight: 1.3,
            }}>
              Tu n'as pas juste un assistant.
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(16px, 2vw, 20px)',
              fontWeight: 800,
              color: C.orangeDark,
              lineHeight: 1.3,
            }}>
              Tu as une équipe complète derrière toi.
            </div>
          </div>
        </div>

        {/* Preuves */}
        {[
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>), label: '12 assistants IA\nspécialisés\nchacun son rôle.' },
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>), label: 'Ils travaillent\nensemble\npour toi.' },
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>), label: '24h/24, 7j/7\nton business\nne s\'arrête jamais.' },
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 12 15 16 10"/></svg>), label: 'Sécurisé,\nfiable et aligné\nsur tes objectifs.' },
        ].map((proof, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            fontSize: 13,
            fontWeight: 600,
            color: C.navyDark,
            lineHeight: 1.35,
          }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: C.cream,
              border: `1px solid ${C.peachBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.orangeDark,
              flexShrink: 0,
            }}>
              {proof.icon}
            </div>
            <div style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{proof.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Note finale */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        style={{
          marginTop: 24,
          fontSize: 15,
          color: 'rgba(26, 42, 74, 0.6)',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span style={{ color: '#e74c3c' }}>❤</span>
        Une vraie équipe. Pas juste de la technologie.
      </motion.p>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 5 — ACQUISITION & VISIBILITÉ (2 blocs - Nouveau design)
// ═══════════════════════════════════════════════════════════════════════════════
const AcquisitionSection = () => (
  <section id="acquisition" style={{ 
    padding: '80px 20px',
    background: `linear-gradient(180deg, #fef5f0 0%, ${C.cream} 100%)`,
    textAlign: 'center',
  }}>
    {/* CSS Responsive */}
    <style>{`
      .acquisition-two-blocks {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 28px;
        max-width: 1200px;
        margin: 0 auto;
        align-items: stretch;
      }
      .acquisition-block {
        background: #fff;
        border: 1px solid ${C.peachBorder};
        border-radius: 24px;
        padding: 32px;
        text-align: left;
        box-shadow: 0 10px 35px rgba(0,0,0,0.04);
        display: flex;
        flex-direction: column;
      }
      .acquisition-block.highlight {
        border: 2px solid ${C.orangeDark};
        box-shadow: 0 15px 50px rgba(232,101,10,0.12);
      }
      .acquisition-items-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px 24px;
      }
      .acquisition-footer-v2 {
        max-width: 1200px;
        margin: 32px auto 0;
        padding: 28px 32px;
        border-radius: 20px;
        background: #fff;
        border: 1px solid ${C.peachBorder};
        box-shadow: 0 10px 30px rgba(0,0,0,0.04);
        display: grid;
        grid-template-columns: 1.5fr repeat(4, 1fr);
        align-items: center;
        gap: 20px;
      }
      .acquisition-footer-main-v2 {
        display: flex;
        align-items: center;
        gap: 16px;
        padding-right: 24px;
        border-right: 1px solid ${C.peachBorder};
      }
      @media (max-width: 1100px) {
        .acquisition-two-blocks { grid-template-columns: 1fr; gap: 24px; }
        .acquisition-block.highlight { order: -1; }
        .acquisition-footer-v2 { grid-template-columns: 1fr 1fr; }
        .acquisition-footer-main-v2 { 
          grid-column: 1 / -1; 
          border-right: none; 
          border-bottom: 1px solid ${C.peachBorder}; 
          padding-bottom: 20px; 
          padding-right: 0;
        }
      }
      @media (max-width: 768px) {
        .acquisition-items-grid { grid-template-columns: 1fr; gap: 14px; }
      }
      @media (max-width: 640px) {
        .acquisition-footer-v2 { grid-template-columns: 1fr; padding: 24px 20px; gap: 16px; }
        .acquisition-footer-main-v2 { flex-direction: column; text-align: center; align-items: center; }
      }
    `}</style>

    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          borderRadius: 999,
          background: C.peachBorder,
          color: C.orangeDark,
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 20,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
        Acquisition & visibilité
      </motion.div>

      {/* Titre */}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        style={{
          margin: 0,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 800,
          lineHeight: 1.1,
          color: C.navyDark,
        }}
      >
        Tes clients <span style={{ color: C.orangeDark }}>viennent à toi.</span>
      </motion.h2>

      {/* Sous-titre */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        style={{
          marginTop: 20,
          fontSize: 'clamp(15px, 2vw, 17px)',
          color: 'rgba(26, 42, 74, 0.7)',
          lineHeight: 1.6,
          maxWidth: 800,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        Google, marketplace, réseaux, IA, influenceurs et experts digitaux :<br />
        tout est pensé pour te rendre visible et attirer des clients chaque jour.
      </motion.p>

      {/* 2 Blocs principaux */}
      <div className="acquisition-two-blocks" style={{ marginTop: 50 }}>
        {/* BLOC GAUCHE — Tu es visible partout */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="acquisition-block"
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 24 }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: 18,
              background: C.cream,
              border: `1px solid ${C.peachBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.navyDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              {/* Pin marker */}
              <div style={{
                position: 'absolute',
                bottom: -4,
                right: -4,
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: C.orangeDark,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" stroke="#fff" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3" fill={C.orangeDark}/>
                </svg>
              </div>
            </div>
            <div>
              <h3 style={{
                margin: '0 0 10px',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 24,
                fontWeight: 800,
                lineHeight: 1.2,
                color: C.orangeDark,
              }}>
                Tu es visible partout
              </h3>
              <p style={{
                margin: 0,
                fontSize: 14,
                color: 'rgba(26, 42, 74, 0.7)',
                lineHeight: 1.55,
              }}>
                Google, marketplace, réseaux, IA, influenceurs : tout converge vers ta boutique.<br />
                Plus la communauté grandit, plus ta visibilité augmente.
              </p>
            </div>
          </div>

          {/* Grille 2 colonnes */}
          <div className="acquisition-items-grid" style={{ flex: 1 }}>
            {/* Colonne gauche */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Google */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#fff',
                  border: `1px solid ${C.peachBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#4285F4' }}>G</span>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.navyDark }}>Google</div>
                  <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>Sites, fiches, avis, recherche</div>
                </div>
              </div>
              {/* Marketplace */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#fff',
                  border: `1px solid ${C.peachBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: C.orangeDark,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.navyDark }}>Marketplace AbracadaBati</div>
                  <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>Des milliers de visiteurs chaque jour</div>
                </div>
              </div>
              {/* Réseaux sociaux */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#fff',
                  border: `1px solid ${C.peachBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  gap: 2,
                }}>
                  <span style={{ fontSize: 12, color: '#E1306C' }}>📷</span>
                  <span style={{ fontSize: 12, color: '#1877F2' }}>f</span>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.navyDark }}>Réseaux sociaux</div>
                  <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>Contenus, publications, communauté</div>
                </div>
              </div>
            </div>

            {/* Colonne droite */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Toutes les IA */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#fff',
                  border: `1px solid ${C.peachBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: '#10A37F',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.navyDark }}>Toutes les IA</div>
                  <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>ChatGPT, Gemini, Perplexity…</div>
                </div>
              </div>
              {/* Influenceurs */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: '#fff',
                  border: `1px solid ${C.peachBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: C.orangeDark,
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.navyDark }}>Influenceurs & communauté</div>
                  <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>Boostés par des influenceurs et la puissance du réseau</div>
                </div>
              </div>
            </div>
          </div>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: C.orangeDark,
            fontSize: 15,
            fontWeight: 700,
            marginTop: 24,
            paddingTop: 20,
            borderTop: `1px solid ${C.peachBorder}`,
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              fontSize: 12,
              fontWeight: 800,
            }}>✓</span>
            On te trouve là où les clients cherchent.
          </div>
        </motion.article>

        {/* BLOC DROIT — Des experts travaillent pour toi (highlight) */}
        <motion.article
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="acquisition-block highlight"
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20 }}>
              <div style={{
                width: 72,
                height: 72,
                borderRadius: 18,
                background: C.cream,
                border: `1px solid ${C.peachBorder}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                position: 'relative',
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.navyDark} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                {/* Star badge */}
                <div style={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: C.orangeDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{ color: '#fff', fontSize: 14 }}>★</span>
                </div>
              </div>
              <h3 style={{
                margin: 0,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 24,
                fontWeight: 800,
                lineHeight: 1.2,
                color: C.navyDark,
              }}>
                Des experts<br />travaillent pour toi
              </h3>
            </div>
            {/* Badge Inclus */}
            <div style={{
              padding: '10px 14px',
              borderRadius: 12,
              background: C.orangeDark,
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              textAlign: 'center',
              lineHeight: 1.3,
              flexShrink: 0,
            }}>
              Inclus<br />Business<br />& Premium
            </div>
          </div>

          {/* Liste services */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
            {[
              { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M8 8l6 6"/></svg>), title: 'Experts SEO digitaux', desc: 'Articles, optimisation, référencement' },
              { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>), title: 'Création de contenu & blog', desc: 'Pour te positionner et attirer du trafic' },
              { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>), title: 'Webmaster', desc: 'Pages optimisées et landing pages' },
              { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>), title: 'Community managers', desc: 'Présence active sur les réseaux' },
              { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>), title: 'Mise en avant continue', desc: 'Google, marketplace et IA' },
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: C.cream,
                  border: `1px solid ${C.peachBorder}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: C.orangeDark,
                  flexShrink: 0,
                }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.navyDark }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: 'rgba(26, 42, 74, 0.6)' }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: C.orangeDark,
            fontSize: 15,
            fontWeight: 700,
            marginTop: 24,
            paddingTop: 20,
            borderTop: `1px solid ${C.peachBorder}`,
          }}>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              fontSize: 12,
              fontWeight: 800,
            }}>✓</span>
            Ton activité est poussée en continu.
          </div>
        </motion.article>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="acquisition-footer-v2"
      >
        {/* Main */}
        <div className="acquisition-footer-main-v2">
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: C.orangeDark,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            flexShrink: 0,
          }}>★</div>
          <div style={{ textAlign: 'left' }}>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(16px, 2vw, 20px)',
              fontWeight: 800,
              color: C.navyDark,
              lineHeight: 1.3,
            }}>
              Tu n'as pas à chercher des clients.
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(16px, 2vw, 20px)',
              fontWeight: 800,
              color: C.orangeDark,
              lineHeight: 1.3,
            }}>
              Tout est fait pour qu'ils viennent à toi.
            </div>
          </div>
        </div>

        {/* Preuves */}
        {[
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>), label: 'Conçu par un expert\ndu digital ayant généré\njusqu\'à 50 millions\nde vues par an.' },
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>), label: 'Suivi par de grands\ninfluenceurs sur les\nréseaux sociaux.' },
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>), label: 'Référencé dans\ntoutes les IA,\nvisibilité maximale.' },
          { icon: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>), label: 'Résultats durables\net optimisés\nen continu.' },
        ].map((proof, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            fontSize: 12,
            fontWeight: 600,
            color: C.navyDark,
            lineHeight: 1.35,
          }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: C.cream,
              border: `1px solid ${C.peachBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.orangeDark,
              flexShrink: 0,
            }}>
              {proof.icon}
            </div>
            <div style={{ whiteSpace: 'pre-line' }}>{proof.label}</div>
          </div>
        ))}
      </motion.div>

      {/* Note finale */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        style={{
          marginTop: 24,
          fontSize: 15,
          color: 'rgba(26, 42, 74, 0.6)',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span style={{ color: '#e74c3c' }}>❤</span>
        La visibilité d'aujourd'hui. Les clients de demain.
      </motion.p>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 6 — INSTALLATION & CRÉATION (Nouveau design pixel perfect)
// ═══════════════════════════════════════════════════════════════════════════════
const InstallationSection = () => (
  <section id="installation" style={{ 
    padding: '80px 20px',
    background: `linear-gradient(180deg, ${C.cream} 0%, #fff5f0 50%, ${C.cream} 100%)`,
    textAlign: 'center',
  }}>
    {/* CSS Responsive */}
    <style>{`
      .install-steps {
        display: flex;
        align-items: flex-start;
        justify-content: center;
        gap: 0;
        max-width: 1100px;
        margin: 0 auto;
      }
      .install-step {
        flex: 1;
        max-width: 320px;
        background: #fff;
        border: 1px solid ${C.peachBorder};
        border-radius: 20px;
        padding: 28px 24px;
        text-align: center;
        box-shadow: 0 10px 35px rgba(0,0,0,0.04);
      }
      .install-arrow {
        display: flex;
        align-items: center;
        padding: 0 8px;
        color: ${C.orangeDark};
      }
      .install-banner {
        max-width: 1100px;
        margin: 40px auto 0;
        padding: 32px 40px;
        border-radius: 20px;
        background: linear-gradient(135deg, ${C.cream} 0%, #fef0e8 100%);
        border: 1px solid ${C.peachBorder};
        display: flex;
        align-items: center;
        gap: 24px;
      }
      .install-proofs {
        max-width: 1100px;
        margin: 32px auto 0;
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 20px;
      }
      @media (max-width: 1024px) {
        .install-steps { flex-wrap: wrap; gap: 20px; }
        .install-arrow { display: none; }
        .install-step { max-width: 100%; }
        .install-banner { flex-direction: column; text-align: center; }
        .install-proofs { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 640px) {
        .install-proofs { grid-template-columns: 1fr; }
      }
    `}</style>

    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          borderRadius: 999,
          background: C.peachBorder,
          color: C.orangeDark,
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 20,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
        Installation & création
      </motion.div>

      {/* Titre */}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        style={{
          margin: 0,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 'clamp(32px, 5vw, 52px)',
          fontWeight: 800,
          lineHeight: 1.1,
          color: C.navyDark,
        }}
      >
        Tu n'as <span style={{ color: C.navyDark }}>rien à faire.</span><br />
        <span style={{ color: C.orangeDark }}>On s'occupe de tout.</span>
      </motion.h2>

      {/* Sous-titre */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        style={{
          marginTop: 20,
          fontSize: 'clamp(16px, 2vw, 18px)',
          color: 'rgba(26, 42, 74, 0.7)',
          lineHeight: 1.6,
        }}
      >
        En 7 jours, ta boutique est prête.<br />
        Tu arrives, <span style={{ color: C.orangeDark, fontWeight: 600 }}>tout est déjà en place.</span>
      </motion.p>

      {/* 3 Étapes */}
      <div className="install-steps" style={{ marginTop: 50 }}>
        {/* Étape 1 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="install-step"
        >
          {/* Numéro */}
          <div style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: `2px solid ${C.orangeDark}`,
            color: C.orangeDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 18,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            margin: '0 auto 16px',
          }}>1</div>
          
          {/* Illustration */}
          <div style={{
            width: 100,
            height: 80,
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
              <rect x="10" y="5" width="60" height="45" rx="4" stroke={C.navyDark} strokeWidth="2" fill="#fff"/>
              <rect x="10" y="5" width="60" height="12" rx="4" fill={C.peachBorder}/>
              <circle cx="18" cy="11" r="2" fill="#ef4444"/>
              <circle cx="26" cy="11" r="2" fill="#eab308"/>
              <circle cx="34" cy="11" r="2" fill="#22c55e"/>
              <rect x="18" y="25" width="35" height="4" rx="2" fill={C.orangeDark}/>
              <rect x="18" y="33" width="25" height="3" rx="1.5" fill={C.peachBorder}/>
              <rect x="18" y="40" width="30" height="3" rx="1.5" fill={C.peachBorder}/>
              <circle cx="62" cy="45" r="10" fill={C.orangeDark} fillOpacity="0.2"/>
              <path d="M58 45l4-8h8l-4 8h-8z" fill={C.orangeDark}/>
            </svg>
          </div>

          {/* Titre */}
          <h3 style={{
            margin: '0 0 12px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 20,
            fontWeight: 800,
            color: C.navyDark,
          }}>On crée tout</h3>

          {/* Description */}
          <p style={{
            fontSize: 14,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
            marginBottom: 20,
          }}>
            On crée ta boutique complète :<br />
            design, pages, services, produits,<br />
            identité visuelle.
          </p>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 16px',
            background: C.cream,
            borderRadius: 12,
            color: C.orangeDark,
            fontSize: 14,
            fontWeight: 700,
          }}>
            <span style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
            }}>✓</span>
            Tu arrives avec une base solide.
          </div>
        </motion.div>

        {/* Flèche 1→2 */}
        <div className="install-arrow">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </div>

        {/* Étape 2 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="install-step"
        >
          {/* Numéro */}
          <div style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: `2px solid ${C.orangeDark}`,
            color: C.orangeDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 18,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            margin: '0 auto 16px',
          }}>2</div>
          
          {/* Illustration */}
          <div style={{
            width: 100,
            height: 80,
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
              {/* Puzzle pieces */}
              <rect x="15" y="10" width="22" height="22" rx="4" stroke={C.navyDark} strokeWidth="2" fill="#fff"/>
              <rect x="43" y="10" width="22" height="22" rx="4" stroke={C.navyDark} strokeWidth="2" fill="#fff"/>
              <rect x="15" y="35" width="22" height="22" rx="4" stroke={C.navyDark} strokeWidth="2" fill="#fff"/>
              <rect x="43" y="35" width="22" height="22" rx="4" stroke={C.navyDark} strokeWidth="2" fill={C.peachBorder}/>
              {/* Icons inside */}
              <circle cx="26" cy="21" r="6" fill={C.peachBorder}/>
              <path d="M24 21h4M26 19v4" stroke={C.navyDark} strokeWidth="1.5"/>
              <rect x="50" y="16" width="8" height="10" rx="1" fill={C.navyDark}/>
              <circle cx="26" cy="46" r="4" stroke={C.orangeDark} strokeWidth="2"/>
              <circle cx="54" cy="46" r="6" fill={C.orangeDark}/>
              <path d="M51 46l2 2 4-4" stroke="#fff" strokeWidth="1.5"/>
            </svg>
          </div>

          {/* Titre */}
          <h3 style={{
            margin: '0 0 12px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 20,
            fontWeight: 800,
            color: C.navyDark,
          }}>On connecte tout</h3>

          {/* Description */}
          <p style={{
            fontSize: 14,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
            marginBottom: 20,
          }}>
            On connecte tous tes outils :<br />
            paiement, domaine, IA,<br />
            visibilité, automatisations…
          </p>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 16px',
            background: C.cream,
            borderRadius: 12,
            color: C.orangeDark,
            fontSize: 14,
            fontWeight: 700,
          }}>
            <span style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
            }}>✓</span>
            Tout fonctionne dès le départ.
          </div>
        </motion.div>

        {/* Flèche 2→3 */}
        <div className="install-arrow">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
          </svg>
        </div>

        {/* Étape 3 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="install-step"
        >
          {/* Numéro */}
          <div style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: `2px solid ${C.orangeDark}`,
            color: C.orangeDark,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 18,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            margin: '0 auto 16px',
          }}>3</div>
          
          {/* Illustration */}
          <div style={{
            width: 100,
            height: 80,
            margin: '0 auto 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg width="80" height="60" viewBox="0 0 80 60" fill="none">
              {/* Rocket */}
              <path d="M40 8c-8 8-12 20-12 32h24c0-12-4-24-12-32z" fill={C.peachBorder} stroke={C.navyDark} strokeWidth="2"/>
              <circle cx="40" cy="25" r="5" fill={C.orangeDark}/>
              <path d="M28 40l-8 8M52 40l8 8" stroke={C.orangeDark} strokeWidth="2"/>
              <rect x="34" y="40" width="12" height="8" fill={C.navyDark}/>
              {/* Flames */}
              <path d="M36 48c-2 6 2 10 4 10s6-4 4-10h-8z" fill={C.orangeDark}/>
              {/* Chart going up */}
              <path d="M55 50l5-10 5 5 8-15" stroke={C.orangeDark} strokeWidth="2" strokeLinecap="round"/>
              <circle cx="73" cy="30" r="4" fill={C.orangeDark}/>
              <path d="M71 30l2 2 3-4" stroke="#fff" strokeWidth="1.5"/>
            </svg>
          </div>

          {/* Titre */}
          <h3 style={{
            margin: '0 0 12px',
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 20,
            fontWeight: 800,
            color: C.navyDark,
          }}>Tu lances</h3>

          {/* Description */}
          <p style={{
            fontSize: 14,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
            marginBottom: 20,
          }}>
            Tu arrives. Tout tourne déjà :<br />
            clients, messages, rendez-vous,<br />
            visibilité, IA…
          </p>

          {/* Résultat */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '12px 16px',
            background: C.cream,
            borderRadius: 12,
            color: C.orangeDark,
            fontSize: 14,
            fontWeight: 700,
          }}>
            <span style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: C.orangeDark,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
            }}>✓</span>
            Tu te concentres sur ton métier.
          </div>
        </motion.div>
      </div>

      {/* Bandeau central */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="install-banner"
      >
        <div style={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          background: C.orangeDark,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
          flexShrink: 0,
        }}>★</div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(20px, 2.5vw, 26px)',
            fontWeight: 800,
            color: C.navyDark,
            lineHeight: 1.3,
          }}>
            Tu n'as rien à construire. <span style={{ color: C.orangeDark }}>Tout est déjà prêt.</span>
          </div>
          <p style={{
            margin: '8px 0 0',
            fontSize: 15,
            color: 'rgba(26, 42, 74, 0.7)',
          }}>
            On installe, on optimise, on connecte… tu n'as plus qu'à te concentrer sur tes clients.
          </p>
        </div>
      </motion.div>

      {/* 4 Preuves */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        className="install-proofs"
      >
        {[
          { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/></svg>), title: 'En 7 jours ouvrés', desc: 'Ta boutique est prête et 100% fonctionnelle.' },
          { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>), title: 'Accompagnement dédié', desc: 'Un expert t\'accompagne de A à Z.' },
          { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>), title: 'Sécurisé & fiable', desc: 'Hébergement, sauvegardes, mises à jour : on gère tout.' },
          { icon: (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><text x="8" y="12" fontSize="8" fill="currentColor" fontWeight="700">24/7</text></svg>), title: 'Support premium', desc: 'Une équipe réactive toujours là pour toi.' },
        ].map((proof, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: 16,
            background: '#fff',
            borderRadius: 14,
            border: `1px solid ${C.peachBorder}`,
          }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: C.cream,
              border: `1px solid ${C.peachBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: C.orangeDark,
              flexShrink: 0,
            }}>
              {proof.icon}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.navyDark, marginBottom: 4 }}>{proof.title}</div>
              <div style={{ fontSize: 13, color: 'rgba(26, 42, 74, 0.6)', lineHeight: 1.4 }}>{proof.desc}</div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Note finale */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        style={{
          marginTop: 32,
          fontSize: 16,
          color: C.navyDark,
          fontWeight: 500,
        }}
      >
        <span style={{ color: '#e74c3c' }}>❤</span> Tu n'as rien à faire. On le fait pour toi. <span style={{ color: C.orangeDark, fontWeight: 700 }}>Simple.</span>
      </motion.p>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 7 — TON SITE. TON DOMAINE. TOUT EST INCLUS. (Block 7)
// ═══════════════════════════════════════════════════════════════════════════════
const SiteDomaineInclusSection = () => (
  <section id="domaine-inclus" style={{
    padding: '80px 20px',
    background: C.cream,
    textAlign: 'center',
  }}>
    {/* CSS Responsive */}
    <style>{`
      .domaine-cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
        max-width: 1100px;
        margin: 0 auto;
      }
      .domaine-card {
        background: #fff;
        border: 1px solid ${C.peachBorder};
        border-radius: 20px;
        padding: 32px 28px;
        text-align: left;
        box-shadow: 0 8px 30px rgba(0,0,0,0.04);
        display: flex;
        flex-direction: column;
      }
      .domaine-card.premium {
        background: linear-gradient(145deg, ${C.navyDark} 0%, #0f1f35 100%);
        border: 2px solid ${C.orangeDark};
        position: relative;
      }
      .domaine-bottom-banner {
        max-width: 1100px;
        margin: 40px auto 0;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
      }
      .domaine-bottom-card {
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 24px 32px;
        background: #fff;
        border-radius: 16px;
        border: 1px solid ${C.peachBorder};
      }
      @media (max-width: 1024px) {
        .domaine-cards { grid-template-columns: 1fr; gap: 20px; }
        .domaine-card.premium { order: -1; }
        .domaine-bottom-banner { grid-template-columns: 1fr; }
      }
    `}</style>

    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 18px',
          borderRadius: 999,
          background: C.orangeDark,
          color: '#fff',
          fontWeight: 700,
          fontSize: 13,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          marginBottom: 24,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
        </svg>
        Offert avec ton abonnement annuel
      </motion.div>

      {/* Titre */}
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.1 }}
        style={{
          margin: 0,
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: 'clamp(36px, 5vw, 56px)',
          fontWeight: 800,
          lineHeight: 1.1,
          color: C.navyDark,
        }}
      >
        Ton site. Ton domaine.<br />
        <span style={{ color: C.orangeDark }}>Tout est inclus.</span>
      </motion.h2>

      {/* Sous-titre */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        style={{
          marginTop: 20,
          marginBottom: 50,
          fontSize: 'clamp(16px, 2vw, 18px)',
          color: 'rgba(26, 42, 74, 0.7)',
          lineHeight: 1.6,
          maxWidth: 700,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        Que tu partes de zéro ou que tu aies déjà un site, on te donne<br />
        une présence digitale complète, prête à fonctionner.
      </motion.p>

      {/* 3 Cartes */}
      <div className="domaine-cards">
        {/* Carte 1: Tu n'as pas de site */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="domaine-card"
        >
          {/* Header avec icône et titre */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 8 }}>
            {/* Globe icon */}
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: '#e8f5e8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10"/>
                <path d="M2 12h20"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div>
              <h3 style={{
                margin: 0,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 22,
                fontWeight: 800,
                color: C.navyDark,
                lineHeight: 1.2,
              }}>
                Tu n'as pas<br />de site
              </h3>
            </div>
          </div>

          {/* Ligne de séparation */}
          <div style={{ height: 3, width: 40, background: C.orangeDark, borderRadius: 2, margin: '16px 0' }}/>

          {/* Description */}
          <p style={{
            fontSize: 15,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
            marginBottom: 24,
          }}>
            On crée ton site complet avec<br />
            ton nom de domaine.
          </p>

          {/* Checklist */}
          <div style={{ marginBottom: 24, flex: 1 }}>
            {[
              { text: 'Nom de domaine offert ', highlight: '1 an' },
              { text: 'Site complet professionnel', highlight: null },
              { text: 'Prêt à l\'emploi', highlight: null },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
              }}>
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: C.orangeDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <span style={{ fontSize: 15, color: C.navyDark }}>
                  {item.text}
                  {item.highlight && <span style={{ color: C.orangeDark, fontWeight: 700 }}>{item.highlight}</span>}
                </span>
              </div>
            ))}
          </div>

          {/* Footer card */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: C.cream,
            borderRadius: 12,
          }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
              </svg>
            </div>
            <span style={{ fontSize: 14, color: 'rgba(26, 42, 74, 0.7)', lineHeight: 1.4 }}>
              Ton site est en ligne rapidement,<br />tu n'as rien à gérer.
            </span>
          </div>
        </motion.div>

        {/* Carte 2: Tu as déjà un site */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="domaine-card"
        >
          {/* Header avec icône et titre */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 8 }}>
            {/* Link icon */}
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: '#e0f2fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.8">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
            </div>
            <div>
              <h3 style={{
                margin: 0,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 22,
                fontWeight: 800,
                color: C.navyDark,
                lineHeight: 1.2,
              }}>
                Tu as déjà<br />un site
              </h3>
            </div>
          </div>

          {/* Ligne de séparation */}
          <div style={{ height: 3, width: 40, background: C.orangeDark, borderRadius: 2, margin: '16px 0' }}/>

          {/* Description */}
          <p style={{
            fontSize: 15,
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
            marginBottom: 24,
          }}>
            On ajoute ta boutique AbracadaBati<br />
            sans toucher à ton site existant.
          </p>

          {/* Checklist */}
          <div style={{ marginBottom: 24, flex: 1 }}>
            {[
              { text: 'Ta boutique intégrée en sous-domaine', highlight: null },
              { text: 'Aucune modification de ton site', highlight: null },
              { text: 'Aucune perte de trafic', highlight: null },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
              }}>
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <span style={{ fontSize: 15, color: C.navyDark }}>{item.text}</span>
              </div>
            ))}
          </div>

          {/* Footer card */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: C.cream,
            borderRadius: 12,
          }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
            <span style={{ fontSize: 14, color: 'rgba(26, 42, 74, 0.7)', lineHeight: 1.4 }}>
              Ton site reste intact,<br />tu gagnes une nouvelle porte d'entrée.
            </span>
          </div>
        </motion.div>

        {/* Carte 3: Tu veux les deux (Premium) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="domaine-card premium"
        >
          {/* Badge LE PLUS COMPLET */}
          <div style={{
            position: 'absolute',
            top: -14,
            right: 20,
            padding: '8px 16px',
            background: C.orangeDark,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Le plus complet
            </span>
          </div>

          {/* Header avec icône et titre */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 8, marginTop: 8 }}>
            {/* Rocket icon */}
            <div style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: 'rgba(249, 115, 22, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" fill="#f97316"/>
                <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" stroke="#fff" strokeWidth="1.5" fill="none"/>
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" stroke="#fff" strokeWidth="1.5"/>
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" stroke="#fff" strokeWidth="1.5"/>
                <circle cx="16" cy="8" r="2" fill="#f97316"/>
              </svg>
            </div>
            <div>
              <h3 style={{
                margin: 0,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 22,
                fontWeight: 800,
                color: '#fff',
                lineHeight: 1.2,
              }}>
                Tu veux<br />les deux
              </h3>
            </div>
          </div>

          {/* Description */}
          <p style={{
            fontSize: 15,
            color: 'rgba(255, 255, 255, 0.7)',
            lineHeight: 1.6,
            marginTop: 16,
            marginBottom: 24,
          }}>
            Ton site existant + ta boutique +<br />
            un domaine complémentaire.
          </p>

          {/* Checklist */}
          <div style={{ marginBottom: 24, flex: 1 }}>
            {[
              'Plusieurs points d\'entrée',
              'Visibilité maximale',
              'Stratégie complète',
            ].map((text, i) => (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 12,
              }}>
                <div style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: C.orangeDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <span style={{ fontSize: 15, color: '#fff' }}>{text}</span>
              </div>
            ))}
          </div>

          {/* Footer card */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '14px 16px',
            background: 'rgba(249, 115, 22, 0.15)',
            borderRadius: 12,
            border: '1px solid rgba(249, 115, 22, 0.3)',
          }}>
            <div style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: C.orangeDark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <path d="M3 3v18h18"/>
                <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
              </svg>
            </div>
            <span style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.4 }}>
              Tu multiplies ta visibilité<br />et tes opportunités.
            </span>
          </div>
        </motion.div>
      </div>

      {/* Bandeau bas - 2 colonnes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        className="domaine-bottom-banner"
      >
        {/* Colonne 1: Nom de domaine offert 1 an */}
        <div className="domaine-bottom-card">
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#e8f5e8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8">
              <circle cx="12" cy="12" r="10"/>
              <path d="M2 12h20"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
          <div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 20,
              fontWeight: 800,
              color: C.navyDark,
              lineHeight: 1.3,
            }}>
              Nom de domaine offert <span style={{ color: C.orangeDark }}>1 an</span><br />
              <span style={{ fontWeight: 500, fontSize: 16 }}>avec abonnement annuel.</span>
            </div>
          </div>
        </div>

        {/* Colonne 2: 5 ans offerts */}
        <div className="domaine-bottom-card">
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: '#e0f7fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0891b2" strokeWidth="1.8">
              <rect x="3" y="8" width="18" height="12" rx="2"/>
              <path d="M12 8V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v2"/>
              <path d="M18 8V6a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v2"/>
              <path d="M12 12v4"/>
              <path d="M12 12h4"/>
            </svg>
          </div>
          <div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 20,
              fontWeight: 800,
              color: C.orangeDark,
              lineHeight: 1.3,
            }}>
              5 ans offerts
            </div>
            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              color: C.navyDark,
              lineHeight: 1.3,
            }}>
              pour les 100 premiers<br />
              Membres Fondateurs.
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 7-OLD — LE NOM DE DOMAINE (ANCIENNE VERSION - À SUPPRIMER)
// ═══════════════════════════════════════════════════════════════════════════════
const DomaineSection = () => (
  <section id="domaine" className="boutique-section" style={{ background: C.cream, display: 'none' }}>
    <div className="boutique-container">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 64 }}>
        <span className="boutique-label" style={{ background: 'rgba(232, 101, 10, 0.1)', color: C.orangeDark }}>
          Ton identité en ligne
        </span>
        <h2 className="boutique-h2" style={{ color: C.navyDark }}>
          Ton nom de domaine.<br />
          <span style={{ color: C.orangeDark }}>Offert.</span>
        </h2>
        <p className="boutique-lead" style={{ color: '#64748b', margin: '0 auto' }}>
          Tu n'as pas de site internet ? On t'en crée un. 
          Tu as déjà un site ? On crée ta boutique AbracadaBati en plus.
        </p>
      </div>

      {/* 3 Cards */}
      <div
        className="boutique-grid-3"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 24,
          marginBottom: 48,
        }}
      >
        {DOMAINE_OPTIONS.map((opt, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            style={{
              background: opt.dark ? `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDark} 100%)` : C.white,
              border: opt.dark ? 'none' : `1px solid ${C.peachBorder}`,
              borderRadius: 20,
              padding: 32,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 20 }}>{opt.emoji}</div>
            <h4 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 20,
              color: opt.dark ? C.white : C.navyDark,
              marginBottom: 12,
            }}>
              {opt.title}
            </h4>
            <p style={{
              fontSize: 14,
              color: opt.dark ? 'rgba(255, 248, 244, 0.6)' : '#64748b',
              lineHeight: 1.7,
              marginBottom: 20,
              flex: 1,
            }}>
              {opt.desc}
            </p>
            <span style={{
              display: 'inline-block',
              padding: '8px 16px',
              borderRadius: 999,
              background: opt.badgeColor === C.green ? 'rgba(34, 197, 94, 0.15)' : 'rgba(232, 101, 10, 0.15)',
              color: opt.badgeColor,
              fontSize: 12,
              fontWeight: 600,
              alignSelf: 'flex-start',
            }}>
              {opt.badge}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Texte final */}
      <p style={{
        textAlign: 'center',
        fontSize: 15,
        color: C.navyDark,
        lineHeight: 1.7,
      }}>
        Dans tous les cas — ton nom de domaine est <strong>offert pendant 1 an</strong> sur abonnement annuel.<br />
        <strong style={{ color: C.green }}>5 ans offerts</strong> pour les 100 premiers Membres Fondateurs.
      </p>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 8 — OFFRES FONDATEUR (Block 8 - Pricing Cards)
// ═══════════════════════════════════════════════════════════════════════════════
const OffresFondateurSection = () => {
  const [placesRestantes] = useState(127); // TODO: Connecter à l'API

  const plans = [
    {
      id: 'gratuit',
      name: 'GRATUIT',
      subtitle: 'Tu découvres',
      price: '0',
      priceOld: null,
      period: 'pour toujours',
      badge: null,
      color: '#22c55e',
      iconBg: '#dcfce7',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="8" width="18" height="12" rx="2" stroke="#22c55e" strokeWidth="1.8"/>
          <path d="M12 8V6a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v2" stroke="#22c55e" strokeWidth="1.8"/>
          <path d="M18 8V6a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v2" stroke="#22c55e" strokeWidth="1.8"/>
          <path d="M12 12v4" stroke="#22c55e" strokeWidth="1.8"/>
          <path d="M12 12h4" stroke="#22c55e" strokeWidth="1.8"/>
        </svg>
      ),
      features: [
        'Accès marketplace',
        'Prestations',
        'CRM',
        'Super Assistant IA',
      ],
      footerText: null,
      cta: 'Commencer gratuitement',
      ctaStyle: 'outline',
      highlighted: false,
    },
    {
      id: 'pro',
      name: 'PRO',
      subtitle: 'Tu lances ton activité',
      price: '50',
      priceOld: '75',
      period: '/mois',
      badge: 'OFFRE FONDATEUR',
      badgeColor: C.navyDark,
      color: '#3b82f6',
      iconBg: '#dbeafe',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" fill="#93c5fd"/>
          <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" stroke="#3b82f6" strokeWidth="1.8"/>
          <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" stroke="#3b82f6" strokeWidth="1.8"/>
          <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" stroke="#3b82f6" strokeWidth="1.8"/>
        </svg>
      ),
      features: [
        'Jusqu\'à 100 produits',
        'Prestations',
        'Assistant IA',
        'Nom de domaine offert 1 an',
      ],
      footerText: 'Ton tarif ne changera jamais',
      cta: 'Choisir Pro',
      ctaStyle: 'filled-navy',
      highlighted: false,
    },
    {
      id: 'business',
      name: 'BUSINESS',
      subtitle: 'Tu accélères',
      price: '75',
      priceOld: '100',
      period: '/mois',
      badge: 'LE PLUS CHOISI',
      badgeColor: C.orangeDark,
      color: C.orangeDark,
      iconBg: '#fef3c7',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M3 21h18" stroke={C.orangeDark} strokeWidth="1.8"/>
          <path d="M5 21V7l7-4 7 4v14" stroke={C.orangeDark} strokeWidth="1.8"/>
          <path d="M9 21v-4h6v4" stroke={C.orangeDark} strokeWidth="1.8"/>
          <path d="M9 9h.01M12 9h.01M15 9h.01M9 13h.01M12 13h.01M15 13h.01" stroke={C.orangeDark} strokeWidth="2" strokeLinecap="round"/>
        </svg>
      ),
      features: [
        'Jusqu\'à 1000 produits',
        'Assistant + 4 agents IA',
        'Automatisation',
        'Organisation complète',
      ],
      footerText: 'Tarif fondateur bloqué à vie',
      cta: 'Choisir Business',
      ctaStyle: 'filled-orange',
      highlighted: true,
    },
    {
      id: 'premium',
      name: 'PREMIUM',
      subtitle: 'Tu passes à un autre niveau',
      price: '100',
      priceOld: '150',
      period: '/mois',
      badge: 'OFFRE FONDATEUR',
      badgeColor: C.navyDark,
      color: '#8b5cf6',
      iconBg: '#f3e8ff',
      icon: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#8b5cf6" strokeWidth="1.8"/>
          <path d="M2 17l10 5 10-5" stroke="#8b5cf6" strokeWidth="1.8"/>
          <path d="M2 12l10 5 10-5" stroke="#8b5cf6" strokeWidth="1.8"/>
        </svg>
      ),
      features: [
        'Jusqu\'à 10 000 produits',
        'Assistant + 12 agents IA',
        'Dream Team complète',
        'Puissance maximale',
      ],
      footerText: 'Tarif fondateur bloqué à vie',
      cta: 'Choisir Premium',
      ctaStyle: 'filled-orange',
      highlighted: false,
    },
  ];

  return (
    <section id="offres" style={{
      padding: '80px 20px 60px',
      background: C.cream,
      textAlign: 'center',
    }}>
      {/* CSS Responsive */}
      <style>{`
        .offres-cards {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          max-width: 1200px;
          margin: 0 auto;
          align-items: stretch;
        }
        .offre-card {
          background: #fff;
          border: 1px solid ${C.peachBorder};
          border-radius: 20px;
          padding: 28px 24px;
          text-align: center;
          display: flex;
          flex-direction: column;
          position: relative;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .offre-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
        }
        .offre-card.highlighted {
          border: 2px solid ${C.orangeDark};
          box-shadow: 0 12px 40px rgba(232, 101, 10, 0.15);
          transform: scale(1.02);
          z-index: 2;
        }
        .offre-card.highlighted:hover {
          transform: scale(1.02) translateY(-4px);
        }
        .offres-commission-banner {
          max-width: 1200px;
          margin: 40px auto 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
        .offres-urgency-banner {
          max-width: 1200px;
          margin: 32px auto 0;
          background: linear-gradient(135deg, ${C.navyDark} 0%, #0f1f35 100%);
          border-radius: 16px;
          padding: 20px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .offres-info-line {
          max-width: 1200px;
          margin: 24px auto 0;
          display: flex;
          justify-content: center;
          gap: 48px;
        }
        @media (max-width: 1100px) {
          .offres-cards { grid-template-columns: repeat(2, 1fr); }
          .offre-card.highlighted { transform: none; }
          .offre-card.highlighted:hover { transform: translateY(-4px); }
        }
        @media (max-width: 640px) {
          .offres-cards { grid-template-columns: 1fr; }
          .offres-commission-banner { grid-template-columns: 1fr; }
          .offres-urgency-banner { flex-direction: column; gap: 16px; text-align: center; }
          .offres-info-line { flex-direction: column; gap: 16px; align-items: center; }
        }
      `}</style>

      <div style={{ maxWidth: 1300, margin: '0 auto' }}>
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            borderRadius: 999,
            background: C.orangeDark,
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 24,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5m14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
          </svg>
          Offre fondateur limitée
        </motion.div>

        {/* Titre */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          style={{
            margin: 0,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: 800,
            lineHeight: 1.1,
            color: C.navyDark,
          }}
        >
          Choisis ton niveau.<br />
          <span style={{ color: C.orangeDark }}>On s'occupe du reste.</span>
        </motion.h2>

        {/* Sous-titre */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          style={{
            marginTop: 20,
            marginBottom: 50,
            fontSize: 'clamp(16px, 2vw, 18px)',
            color: 'rgba(26, 42, 74, 0.7)',
            lineHeight: 1.6,
          }}
        >
          Profite d'un tarif bloqué à vie.<br />
          Réservé aux <span style={{ color: C.orangeDark, fontWeight: 700 }}>200 premiers membres</span>.
        </motion.p>

        {/* 4 Cartes */}
        <div className="offres-cards">
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className={`offre-card ${plan.highlighted ? 'highlighted' : ''}`}
            >
              {/* Badge "LE PLUS CHOISI" */}
              {plan.badge && (
                <div style={{
                  position: 'absolute',
                  top: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '6px 14px',
                  background: plan.badgeColor,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  whiteSpace: 'nowrap',
                }}>
                  {plan.badge === 'LE PLUS CHOISI' && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  )}
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Icône */}
              <div style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                background: plan.iconBg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                {plan.icon}
              </div>

              {/* Nom du plan */}
              <h3 style={{
                margin: 0,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 22,
                fontWeight: 800,
                color: plan.color,
                letterSpacing: 1,
              }}>
                {plan.name}
              </h3>

              {/* Sous-titre */}
              <p style={{
                margin: '4px 0 20px',
                fontSize: 14,
                color: 'rgba(26, 42, 74, 0.6)',
              }}>
                {plan.subtitle}
              </p>

              {/* Prix */}
              <div style={{ marginBottom: 16 }}>
                {plan.priceOld && (
                  <span style={{
                    fontSize: 20,
                    color: '#9ca3af',
                    textDecoration: 'line-through',
                    marginRight: 8,
                  }}>
                    {plan.priceOld}€
                  </span>
                )}
                <span style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 48,
                  fontWeight: 800,
                  color: C.navyDark,
                }}>
                  {plan.price}€
                </span>
                <span style={{
                  fontSize: 16,
                  color: 'rgba(26, 42, 74, 0.6)',
                }}>
                  {plan.period}
                </span>
              </div>

              {/* Badge Offre Fondateur sous le prix (seulement pour Pro, Business, Premium) */}
              {plan.badge && (
                <div style={{
                  display: 'inline-flex',
                  padding: '6px 12px',
                  background: plan.badge === 'LE PLUS CHOISI' ? C.orangeDark : C.navyDark,
                  borderRadius: 6,
                  marginBottom: 20,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {plan.badge === 'LE PLUS CHOISI' ? 'OFFRE FONDATEUR' : plan.badge}
                  </span>
                </div>
              )}

              {/* Features */}
              <div style={{ flex: 1, marginBottom: 20, textAlign: 'left' }}>
                {plan.features.map((feature, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 12,
                  }}>
                    <div style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: plan.highlighted ? C.orangeDark : plan.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                    <span style={{ fontSize: 14, color: C.navyDark }}>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Footer text */}
              {plan.footerText && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  background: plan.highlighted ? 'rgba(232, 101, 10, 0.08)' : C.cream,
                  borderRadius: 10,
                  marginBottom: 16,
                  border: plan.highlighted ? `1px solid ${C.orangeDark}` : 'none',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill={plan.highlighted ? C.orangeDark : C.navyDark}>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" fill="none" stroke={plan.highlighted ? C.orangeDark : C.navyDark} strokeWidth="2"/>
                  </svg>
                  <span style={{ fontSize: 13, color: plan.highlighted ? C.orangeDark : C.navyDark, fontWeight: 600 }}>
                    {plan.footerText}
                  </span>
                </div>
              )}

              {/* CTA Button */}
              <button
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: 15,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: plan.ctaStyle === 'outline' ? `2px solid ${C.navyDark}` : 'none',
                  background: plan.ctaStyle === 'outline' 
                    ? 'transparent' 
                    : plan.ctaStyle === 'filled-navy' 
                      ? C.navyDark 
                      : C.orangeDark,
                  color: plan.ctaStyle === 'outline' ? C.navyDark : '#fff',
                }}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Bandeau Commission */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="offres-commission-banner"
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '20px 28px',
            background: '#fff',
            borderRadius: 14,
            border: `1px solid ${C.peachBorder}`,
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 14,
              color: C.orangeDark,
            }}>
              0%
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.orangeDark }}>0% COMMISSION.</div>
              <div style={{ fontSize: 14, color: 'rgba(26, 42, 74, 0.6)' }}>Pour toujours.</div>
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '20px 28px',
            background: '#fff',
            borderRadius: 14,
            border: `1px solid ${C.peachBorder}`,
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: '#fce4d4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.orangeDark} strokeWidth="1.8">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.orangeDark }}>TU GARDES 100%</div>
              <div style={{ fontSize: 14, color: 'rgba(26, 42, 74, 0.6)' }}>de ce que tu gagnes.</div>
            </div>
          </div>
        </motion.div>

        {/* Bandeau Urgence */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="offres-urgency-banner"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: 'rgba(249, 115, 22, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#f97316">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.orangeDark, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Offre limitée aux 200 premiers membres
              </div>
              <div style={{ fontSize: 14, color: 'rgba(255, 255, 255, 0.7)' }}>
                Une fois complet, les tarifs augmentent définitivement.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Places restantes :
            </span>
            <div style={{
              padding: '10px 20px',
              background: '#0f1f35',
              borderRadius: 10,
              border: '1px solid rgba(249, 115, 22, 0.3)',
            }}>
              <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 28, fontWeight: 800, color: '#22c55e' }}>
                {placesRestantes}
              </span>
              <span style={{ fontSize: 28, fontWeight: 800, color: 'rgba(255, 255, 255, 0.4)' }}> / 200</span>
            </div>
          </div>
        </motion.div>

        {/* Ligne d'info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="offres-info-line"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="1.8">
              <circle cx="12" cy="12" r="10"/>
              <path d="M2 12h20"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <span style={{ fontSize: 14, color: C.navyDark }}>
              <strong>Nom de domaine offert 1 an</strong> avec abonnement annuel
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="1.8">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span style={{ fontSize: 14, color: C.navyDark }}>
              <strong>Prix affichés = facturation annuelle</strong> (plus avantageuse)
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 7-OLD — TABLEAU COMPARATIF (Hidden - remplacé par OffresFondateurSection)
// ═══════════════════════════════════════════════════════════════════════════════
const TableauSection = () => {
  const renderCell = (value) => {
    if (value === true) return <span style={{ color: C.green, fontSize: 20 }}>✓</span>;
    if (value === false) return <span style={{ color: '#cbd5e1', fontSize: 16 }}>—</span>;
    return <span style={{ fontWeight: 600, color: C.navyDark }}>{value}</span>;
  };

  return (
    <section id="formules" className="boutique-section" style={{ background: C.white }}>
      <div className="boutique-container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <span className="boutique-label" style={{ background: 'rgba(232, 101, 10, 0.1)', color: C.orangeDark }}>
            Selon ta formule
          </span>
          <h2 className="boutique-h2" style={{ color: C.navyDark }}>
            Ta boutique grandit<br />
            <span style={{ color: C.orangeDark }}>avec toi.</span>
          </h2>
        </div>

        {/* Tableau Desktop */}
        <div className="boutique-table-desktop" style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 14,
          }}>
            <thead>
              <tr style={{ background: C.navyDark }}>
                <th style={{ padding: '16px 20px', textAlign: 'left', color: C.white, fontWeight: 600, borderRadius: '12px 0 0 0' }}>
                  Fonctionnalité
                </th>
                <th style={{ padding: '16px 20px', textAlign: 'center', color: C.white, fontWeight: 600 }}>Gratuit</th>
                <th style={{ padding: '16px 20px', textAlign: 'center', color: C.white, fontWeight: 600 }}>Pro</th>
                <th style={{ padding: '16px 20px', textAlign: 'center', color: C.white, fontWeight: 600 }}>Business</th>
                <th style={{ padding: '16px 20px', textAlign: 'center', color: C.white, fontWeight: 600, borderRadius: '0 12px 0 0', background: `linear-gradient(135deg, ${C.orangeDark} 0%, ${C.orange} 100%)` }}>
                  Premium
                </th>
              </tr>
            </thead>
            <tbody>
              {TABLEAU_FEATURES.map((row, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? C.white : C.cream }}>
                  <td style={{ padding: '14px 20px', fontWeight: 500, color: C.navyDark }}>
                    {row.label}
                  </td>
                  <td style={{ padding: '14px 20px', textAlign: 'center' }}>{renderCell(row.gratuit)}</td>
                  <td style={{ padding: '14px 20px', textAlign: 'center' }}>{renderCell(row.pro)}</td>
                  <td style={{ padding: '14px 20px', textAlign: 'center' }}>{renderCell(row.business)}</td>
                  <td style={{ padding: '14px 20px', textAlign: 'center', background: i % 2 === 0 ? 'rgba(232, 101, 10, 0.04)' : 'rgba(232, 101, 10, 0.08)' }}>
                    {renderCell(row.premium)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Tableau Mobile — Cards */}
        <div className="boutique-table-mobile">
          {['Gratuit', 'Pro', 'Business', 'Premium'].map((plan, pIdx) => (
            <div
              key={plan}
              style={{
                background: plan === 'Premium' ? `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDark} 100%)` : C.white,
                border: plan === 'Premium' ? 'none' : `1px solid ${C.peachBorder}`,
                borderRadius: 16,
                padding: 24,
                marginBottom: 16,
              }}
            >
              <h4 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 700,
                fontSize: 20,
                color: plan === 'Premium' ? C.white : C.navyDark,
                marginBottom: 16,
              }}>
                {plan}
              </h4>
              {TABLEAU_FEATURES.map((row, i) => {
                const value = row[plan.toLowerCase()];
                return (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom: i < TABLEAU_FEATURES.length - 1 ? `1px solid ${plan === 'Premium' ? 'rgba(255,255,255,0.1)' : C.peachBorder}` : 'none',
                  }}>
                    <span style={{ fontSize: 13, color: plan === 'Premium' ? 'rgba(255,255,255,0.7)' : '#64748b' }}>
                      {row.label}
                    </span>
                    <span style={{ fontWeight: 600, color: plan === 'Premium' ? C.white : C.navyDark }}>
                      {value === true ? '✓' : value === false ? '—' : value}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 9 — SERVICE CLÉ EN MAIN (Block 9 - PIXEL PERFECT VERSION)
// ═══════════════════════════════════════════════════════════════════════════════
const ServiceCleEnMainSection = () => {
  return (
    <section 
      id="cle-en-main" 
      style={{
        background: 'linear-gradient(180deg, #f4f9ff 0%, #ffffff 100%)',
        padding: '80px 20px',
        fontFamily: "'DM Sans', sans-serif",
        color: '#1a2a4a',
      }}
    >
      {/* CSS Responsive */}
      <style>{`
        .clem-main-card {
          display: grid;
          grid-template-columns: 1.1fr 1fr;
          gap: 38px;
        }
        .clem-offers-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 22px;
        }
        .clem-benefits-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .clem-final-banner-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
        .clem-info-row {
          display: flex;
          justify-content: center;
          gap: 60px;
        }
        @media (max-width: 1024px) {
          .clem-main-card { grid-template-columns: 1fr; }
          .clem-offers-grid { grid-template-columns: 1fr; }
          .clem-benefits-grid { grid-template-columns: 1fr; }
          .clem-final-banner-grid { grid-template-columns: 1fr; text-align: center; }
          .clem-info-row { flex-direction: column; gap: 16px; align-items: center; }
        }
      `}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
        
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 20px',
            borderRadius: 999,
            background: '#eaf3ff',
            color: '#1264e8',
            fontWeight: 700,
            marginBottom: 20,
            border: '1px solid #bfd8ff',
            fontSize: 13,
          }}
        >
          🚀 SERVICE CLÉ EN MAIN
        </motion.div>

        {/* Titre */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(36px, 5vw, 56px)',
            lineHeight: 1.1,
            margin: 0,
            fontWeight: 800,
            letterSpacing: '-0.02em',
          }}
        >
          Tu arrives. <span style={{ color: '#1264e8' }}>Tout tourne déjà.</span>
        </motion.h2>

        {/* Sous-titre */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          style={{
            fontSize: 'clamp(16px, 2vw, 18px)',
            lineHeight: 1.5,
            margin: '16px auto 40px',
            maxWidth: 650,
            color: 'rgba(26, 42, 74, 0.7)',
            fontWeight: 500,
          }}
        >
          On s'occupe de tout.<br />
          Tu récupères une boutique prête à vendre.
        </motion.p>

        {/* Grande carte centrale */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="clem-main-card"
          style={{
            background: 'white',
            border: '1px solid #cfe2ff',
            borderRadius: 28,
            boxShadow: '0 30px 90px rgba(18, 100, 232, 0.14)',
            padding: 38,
            alignItems: 'center',
            textAlign: 'left',
            marginBottom: 28,
          }}
        >
          {/* Dashboard Mockup */}
          <div style={{
            background: 'linear-gradient(135deg, #eef6ff, #ffffff)',
            borderRadius: 24,
            padding: 28,
            border: '1px solid #d8e9ff',
            minHeight: 300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <div style={{
              width: '100%',
              maxWidth: 520,
              background: 'white',
              borderRadius: 22,
              boxShadow: '0 20px 60px rgba(26, 42, 74, 0.12)',
              padding: 26,
            }}>
              <div style={{ fontWeight: 800, color: '#1a2a4a', marginBottom: 14, fontSize: 18 }}>
                Ma boutique
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 14,
                marginBottom: 18,
              }}>
                <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                  Ventes<br /><strong style={{ fontSize: 20 }}>24 650€</strong>
                </div>
                <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                  Commandes<br /><strong style={{ fontSize: 20 }}>128</strong>
                </div>
                <div style={{ background: '#f4f9ff', borderRadius: 14, padding: 16, fontSize: 14 }}>
                  Clients<br /><strong style={{ fontSize: 20 }}>356</strong>
                </div>
              </div>
              <div style={{
                background: '#e9fff3',
                color: '#15803d',
                display: 'inline-block',
                padding: '6px 12px',
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 13,
              }}>
                En ligne
              </div>
            </div>
          </div>

          {/* Installation info */}
          <div>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 'clamp(26px, 3vw, 34px)',
              lineHeight: 1.1,
              margin: '0 0 22px',
              fontWeight: 800,
            }}>
              Installation clé en main
            </h3>

            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 28px',
              fontSize: 18,
              lineHeight: 1.9,
              fontWeight: 600,
            }}>
              <li>✅ Site créé et personnalisé à ton image</li>
              <li>✅ Boutique prête avec tes produits / prestations</li>
              <li>✅ Agents IA configurés et opérationnels</li>
              <li>✅ Paiements et livraisons connectés</li>
              <li>✅ Automatisations et emails activés</li>
            </ul>

            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              background: '#eaf3ff',
              color: '#1264e8',
              border: '1px solid #bfd8ff',
              borderRadius: 16,
              padding: '16px 24px',
              fontSize: 24,
              fontWeight: 900,
            }}>
              📅 LIVRÉ EN 7 JOURS
            </div>
          </div>
        </motion.div>

        {/* 3 Cartes Offres */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="clem-offers-grid"
          style={{ marginBottom: 26, textAlign: 'left' }}
        >
          {/* PRO */}
          <div style={{
            background: 'white',
            border: '1px solid #cfe2ff',
            borderRadius: 22,
            padding: 28,
            boxShadow: '0 18px 50px rgba(18, 100, 232, 0.10)',
          }}>
            <h3 style={{ color: '#1264e8', fontSize: 30, margin: 0, fontWeight: 900 }}>PRO</h3>
            <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Installation clé en main</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
              <li>✅ Site créé et personnalisé</li>
              <li>✅ Prestations intégrées</li>
              <li>✅ Assistant IA configuré</li>
              <li>✅ Nom de domaine connecté</li>
              <li>✅ Boutique prête à vendre</li>
            </ul>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '1px solid #d8e9ff',
              paddingLeft: 20,
              margin: '20px 0',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>500€</div>
                <div style={{ color: '#1264e8', fontSize: 48, fontWeight: 900 }}>250€</div>
              </div>
            </div>
            <div style={{
              background: '#eaf3ff',
              color: '#1264e8',
              borderRadius: 999,
              display: 'inline-block',
              padding: '6px 14px',
              fontWeight: 800,
              marginBottom: 18,
              fontSize: 12,
            }}>
              OFFRE FONDATEUR
            </div>
            <button style={{
              width: '100%',
              border: 'none',
              borderRadius: 14,
              background: '#1264e8',
              color: 'white',
              padding: 16,
              fontSize: 18,
              fontWeight: 900,
              cursor: 'pointer',
            }}>
              Choisir Pro
            </button>
          </div>

          {/* BUSINESS - Mise en avant */}
          <div style={{
            background: 'white',
            border: '2px solid #E8650A',
            borderRadius: 22,
            padding: 28,
            boxShadow: '0 26px 70px rgba(232, 101, 10, 0.18)',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              top: -18,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#E8650A',
              color: 'white',
              borderRadius: 999,
              padding: '9px 22px',
              fontWeight: 900,
              fontSize: 13,
              whiteSpace: 'nowrap',
            }}>
              ⭐ LE PLUS CHOISI
            </div>
            <h3 style={{ color: '#E8650A', fontSize: 30, margin: 0, fontWeight: 900, marginTop: 8 }}>BUSINESS</h3>
            <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Système complet</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
              <li>✅ Tout ce qui est inclus dans Pro</li>
              <li>✅ 4 agents IA configurés</li>
              <li>✅ Automatisations avancées</li>
              <li>✅ Organisation complète</li>
              <li>✅ Tests et optimisation</li>
            </ul>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '1px solid #fce4d4',
              paddingLeft: 20,
              margin: '20px 0',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>1000€</div>
                <div style={{ color: '#E8650A', fontSize: 48, fontWeight: 900 }}>500€</div>
              </div>
            </div>
            <div style={{
              background: '#fce4d4',
              color: '#E8650A',
              borderRadius: 999,
              display: 'inline-block',
              padding: '6px 14px',
              fontWeight: 800,
              marginBottom: 18,
              fontSize: 12,
            }}>
              OFFRE FONDATEUR
            </div>
            <button style={{
              width: '100%',
              border: 'none',
              borderRadius: 14,
              background: '#E8650A',
              color: 'white',
              padding: 16,
              fontSize: 18,
              fontWeight: 900,
              cursor: 'pointer',
            }}>
              Choisir Business
            </button>
          </div>

          {/* PREMIUM */}
          <div style={{
            background: 'white',
            border: '1px solid #dec8ff',
            borderRadius: 22,
            padding: 28,
            boxShadow: '0 18px 50px rgba(124, 58, 237, 0.12)',
          }}>
            <h3 style={{ color: '#7c3aed', fontSize: 30, margin: 0, fontWeight: 900 }}>PREMIUM</h3>
            <p style={{ margin: '4px 0 20px', fontWeight: 700 }}>Machine complète</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px', lineHeight: 1.8, fontWeight: 600, fontSize: 15 }}>
              <li>✅ Tout ce qui est inclus dans Business</li>
              <li>✅ 12 agents IA configurés</li>
              <li>✅ Dream Team complète</li>
              <li>✅ Stratégie et structure complètes</li>
              <li>✅ Accompagnement prioritaire</li>
            </ul>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderLeft: '1px solid #eadcff',
              paddingLeft: 20,
              margin: '20px 0',
            }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Paiement unique</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ textDecoration: 'line-through', color: '#7a8495', fontSize: 24 }}>1500€</div>
                <div style={{ color: '#7c3aed', fontSize: 48, fontWeight: 900 }}>750€</div>
              </div>
            </div>
            <div style={{
              background: '#eadcff',
              color: '#7c3aed',
              borderRadius: 999,
              display: 'inline-block',
              padding: '6px 14px',
              fontWeight: 800,
              marginBottom: 18,
              fontSize: 12,
            }}>
              OFFRE FONDATEUR
            </div>
            <button style={{
              width: '100%',
              border: 'none',
              borderRadius: 14,
              background: '#7c3aed',
              color: 'white',
              padding: 16,
              fontSize: 18,
              fontWeight: 900,
              cursor: 'pointer',
            }}>
              Choisir Premium
            </button>
          </div>
        </motion.div>

        {/* Bandeau 3 bénéfices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="clem-benefits-grid"
          style={{
            background: 'white',
            border: '1px solid #cfe2ff',
            borderRadius: 22,
            padding: 20,
            marginBottom: 20,
            boxShadow: '0 18px 50px rgba(18, 100, 232, 0.08)',
            textAlign: 'center',
          }}
        >
          <div style={{ padding: '10px 0' }}>
            <strong style={{ fontSize: 16 }}>🕒 Livré en 7 jours</strong><br />
            <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>Ta boutique prête à l'emploi</span>
          </div>
          <div style={{ padding: '10px 0' }}>
            <strong style={{ fontSize: 16 }}>🛡️ Zéro prise de tête</strong><br />
            <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>On s'occupe de tout</span>
          </div>
          <div style={{ padding: '10px 0' }}>
            <strong style={{ fontSize: 16 }}>🎯 Prêt à vendre</strong><br />
            <span style={{ color: 'rgba(26, 42, 74, 0.7)', fontSize: 14 }}>Tu n'as plus qu'à encaisser</span>
          </div>
        </motion.div>

        {/* Bandeau vert fondateur final */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="clem-final-banner-grid"
          style={{
            background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
            color: 'white',
            borderRadius: 20,
            padding: '24px 32px',
            alignItems: 'center',
            textAlign: 'left',
            boxShadow: '0 22px 70px rgba(21, 128, 61, 0.25)',
          }}
        >
          <div style={{ fontSize: 'clamp(18px, 2vw, 24px)', fontWeight: 900 }}>
            🏆 OFFRE RÉSERVÉE AUX 100 PREMIERS<br />
            <span style={{ color: '#fef08a' }}>MEMBRES FONDATEURS</span>
          </div>
          <div style={{ fontSize: 'clamp(16px, 2vw, 22px)', fontWeight: 900 }}>
            🔒 TARIF FONDATEUR BLOQUÉ À VIE<br />
            <span style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255, 255, 255, 0.78)' }}>
              Ton tarif ne changera jamais tant que ton abonnement est actif.
            </span>
          </div>
        </motion.div>

        {/* Ligne d'info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.7 }}
          className="clem-info-row"
          style={{
            marginTop: 24,
            fontWeight: 700,
            color: '#1a2a4a',
            fontSize: 15,
          }}
        >
          <div>🛡️ Paiement sécurisé</div>
          <div>🎧 Support humain 7j/7</div>
          <div>∞ 0% commission — tu gardes 100%</div>
        </motion.div>

      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 10 — CTA FONDATEUR
// ═══════════════════════════════════════════════════════════════════════════════
const CTASection = () => {
  const [placesRestantes] = useState(73); // TODO: Connecter à l'API

  return (
    <section className="boutique-section" style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 50%, ${C.navy} 100%)`, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      {/* Orbes décoratives */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', bottom: '-20%', left: '-10%', width: '500px', height: '500px', borderRadius: '50%', opacity: 0.3, background: 'radial-gradient(circle, rgba(34,197,94,0.4) 0%, transparent 70%)' }} />
        <div style={{ position: 'absolute', top: '-15%', right: '-8%', width: '400px', height: '400px', borderRadius: '50%', opacity: 0.2, background: 'radial-gradient(circle, rgba(249,115,22,0.5) 0%, transparent 70%)' }} />
      </div>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '56px 56px', pointerEvents: 'none' }} />
      
      <div className="boutique-container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{
            display: 'inline-block',
            padding: '10px 20px',
            borderRadius: 999,
            background: 'rgba(34, 197, 94, 0.15)',
            color: C.green,
            fontWeight: 600,
            fontSize: 14,
            marginBottom: 32,
          }}
        >
          👑 Offre Membres Fondateurs — 100 places
        </motion.div>

        {/* Titre */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="boutique-h2"
          style={{ color: C.white, marginBottom: 40 }}
        >
          Ta boutique complète.<br />
          Installée en 7 jours.<br />
          <span style={{ color: C.orangeDark }}>Pour toujours chez toi.</span>
        </motion.h2>

        {/* 3 Points clés */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 32,
            marginBottom: 48,
          }}
        >
          {[
            { emoji: '⚡', text: 'Installation complète offerte' },
            { emoji: '🌐', text: 'Nom de domaine 5 ans offert' },
            { emoji: '💎', text: 'Double AbracaCoins à vie' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              color: C.white,
              fontSize: 16,
              fontWeight: 500,
            }}>
              <span style={{ fontSize: 22 }}>{item.emoji}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <Link
            to="/pro/membre-fondateur"
            className="boutique-btn"
            style={{
              background: C.green,
              color: C.white,
              boxShadow: '0 8px 40px rgba(34, 197, 94, 0.3)',
              padding: '20px 48px',
              fontSize: 18,
            }}
          >
            Devenir Membre Fondateur →
          </Link>
        </motion.div>

        {/* Places restantes */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: 20,
            fontSize: 13,
            color: 'rgba(255, 248, 244, 0.35)',
          }}
        >
          Plus que <strong style={{ color: C.orangeDark }}>{placesRestantes} places</strong> sur les 100
        </motion.p>

        {/* Slogan final */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          style={{
            marginTop: 48,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 700,
            fontStyle: 'italic',
            fontSize: 'clamp(20px, 2.5vw, 28px)',
            color: C.orangeDark,
          }}
        >
          Bienvenue chez toi.
        </motion.p>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ═══════════════════════════════════════════════════════════════════════════════
export default function BoutiquePage() {
  return (
    <ImmersiveEspaceProWrapper>
      <div className="boutique-root" style={{ background: '#072B57' }}>
        <GlobalStyles />
        <UnifiedHeader />
        <EspaceProNav />
        <EspaceProSectionsNav />
        <EspaceProSectionsNavSpacer />
        
        <main style={{ background: '#072B57' }}>
          <HeroSection />
          <BoutiqueExplicationSection />
          <PortesSection />
          <FeaturesSection />
          <EquipeSection />
          <AcquisitionSection />
          <InstallationSection />
          <SiteDomaineInclusSection />
          <OffresFondateurSection />
          <ServiceCleEnMainSection />
        </main>
        
        
      </div>
    </ImmersiveEspaceProWrapper>
  );
}
